import ax from 'axios';
import type { AxiosResponse } from 'axios';
import type { RegisteredEventInfo, EventResponse, PersonWrite, PersonWithRelations, PersonWithEmail, DuplicateGroup, MergePersonsRequest, EmailCreate, EmailRead, EmailUpdate, PhoneCreate, PhoneRead, PhoneUpdate, Address, AddressWrite, AuthStatusResponse, AvailableMethodsResponse, RegisterRequest, AuthenticationResponse, MessageResponse, SessionListResponse, AccountInfo, UpdateAccountRequest, ChangePasswordRequest, SendVerificationRequest, VerifyEmailRequest, PasswordLoginRequest, ForgotPasswordRequest, ResetPasswordRequest, SSOInitiateRequest, SSOInitiateResponse, SSOCallbackRequest, SSOCallbackResponse, SSOLinkRequest, SSOLinkResponse, SSOUnlinkResponse, SSOLoginRequest, IdentityAdminUpdate, WebsiteRead, WebsiteCreate, WebsiteUpdate, BlockManifest, PageCreate, PageRead, PageUpdate, MoveRequest, PageVersionRead, PublishResult, GlobalBlockRead, GlobalBlockUpsert, PageSchemaRead, PageResolveRead, RedirectCreate, RedirectRead, RedirectUpdate, PlausibleSiteCreate, PlausibleSiteRead, PlausibleRealtimeVisitors, PlausibleAggregateMetrics, PlausibleTimeseriesRead, PlausibleBreakdownRead, PlausibleGoalsList, PlausibleGoalCreate, PlausibleGoalRead, PlausibleFunnelsList, CollectionSchemaCreate, CollectionSchemaRead, CollectionSchemaUpdate, CollectionItemCreate, CollectionItemRead, CollectionItemUpdate, FormSubmissionCreate, FormSubmissionRead, FormSubStatus, FormSubmissionUpdate, FormStepUpdate, FormDefinitionCreate, FormDefinitionRead, FormDefinitionUpdate, ResolveNextRequest, ResolveNextResponse, ValidateFormRequest, ValidateFormResponse, EmailTemplateCreate, EmailTemplateResponse, TemplateType, EmailTemplateUpdate, EmailSuppressionResponse, SuppressEmailRequest, CampaignCreate, CampaignResponse, CampaignStatus, CampaignUpdate, CampaignStatsResponse, SendTestEmailRequest, UnsubscribeRequest, FileResponse, AttachRequest, FileAttachmentRead, AttachManyRequest, ToolDefinition, AutomationResponse, AutomationCreate, BuildAutomationRequest, BuilderResponse, AutomationUpdate, AutomationExecutionResponse, TestAutomationRequest, TestAutomationResult, OperatorInfo, AutomationStats, ToolsListResponse, ModelInfo, SkillCreate, SkillResponse, SkillUpdate, ArtifactResponse, ArtifactRename, ArtifactSourceUpdate, ConversationCreate, AgentConversationResponse, ConversationUpdate, ConversationHistoryResponse, SendAgentMessageRequest, StartMessageRunResponse, UpdateMessageRequest, AgentMessageResponse, InjectMessageRequest, CompressContextRequest, CompressContextResponse, PendingInterrupt, ResumeRunRequest, QueueListResponse, UpdateQueueEntryRequest, QueueEntryResponse, DatastoreCreate, DatastoreResponse, DatastoreUpdate, FeedbackCreate, AgentFeedbackRead, FeedbackSource, FeedbackLayer, FeedbackStatus, FeedbackAggregateRow, FeedbackUpdate, FeedbackReproResult, JudgeAttributeResult, JudgeScanResult, McpServerPluginCreate, McpServerPluginResponse, McpServerItem, McpServerPluginUpdate, MemoryCreate, MemoryResponse, MemoryUpdate, ScenarioCreate, ScenarioResponse, ScenarioUpdate, ScenarioMessageData, ScenarioMessageUpdate, AgentCreate, AgentResponse, AgentStatus, AgentUpdate, AgentToolsUpdate, AgentSkillsUpdate, AgentDatastoresUpdate, AgentMcpServersUpdate } from './types.d';
import {
	ApiResponse,
	parseApiError,
	formatFieldErrors,
	useApiRequest,
	useCancellableRequest,
	unwrap,
	createSSEStreamPost, createSSEStream, type StreamController, type SSEStreamOptions,
	type ApiConfig,
	type ParsedError,
	type FastAPIValidationError,
	type FastAPIErrorResponse,
	type ResponseMetadata,
	type ApiRequestState,
	type RequestInterceptor,
	type ResponseInterceptor,
	type Unwrapped
} from '@bagelink/sdk';
import { ref, type Ref } from 'vue';

/**
 * Options for file upload operations
 */
export interface UploadOptions {
	/** Progress callback for upload operations */
	onUploadProgress?: (progressEvent: any) => void
	/** Directory path for the uploaded file */
	dirPath?: string
	/** Tags to associate with the uploaded file */
	tags?: string[]
}


// ============================================================================
// Stream Event Type Definitions (Fully Typed!)
// ============================================================================

/**
 * Event types for AgentsConversationsMessagesStream stream
 * Events: "done", "error", "middleware", "reasoning", "skill_activated", "token", "tool_call", "tool_result", "user_message_saved"
 *
 * - **user_message_saved**: Event data
 * - **token**: Event data
 * - **done**: Event data
 */
export interface AgentsConversationsMessagesStreamStreamEvents {
  /**
   * user_message_saved
   * - `message_id`: Field data
   */
  user_message_saved: {
    /** message_id */
    message_id: any
  }
  /**
   * token
   * - `delta`: Field data
   * - `message_id`: Field data
   * - `role`: Field data
   */
  token: {
    /** delta */
    delta: any
    /** message_id */
    message_id: any
    /** role */
    role: any
  }
  /**
   * done
   * - `reason`: Field data
   * - `message_id`: Field data
   * - `usage`: Field data
   */
  done: {
    /** reason */
    reason: any
    /** message_id */
    message_id: any
    /** usage */
    usage: any
  }
}



/**
 * Configured axios instance for API requests
 */
export const axios = ax.create({baseURL: '/api', withCredentials: true, paramsSerializer: { indexes: null }});


/**
 * API functions for events endpoints
 */
export const events = {
  get: /**
 * List Available Events
 *
 * List all available events that can be emitted.
 * 
 * Shows events registered via create_emitter() including:
 * - Event name
 * - Description
 * - Source module
 * - Example payload
 * - Payload JSON Schema
 * - Number of subscribers
 *
 * @endpoint GET /events
 */
async (): Promise<ApiResponse<RegisteredEventInfo[]>> => {
        const response = await axios.get('/events')
        return ApiResponse.create(response.data, response)},
  log: {
    getByEventId: /**
 * Get Event Details
 *
 * Get details of a specific event execution.
 * 
 * Returns the event data and all handler executions in one response.
 *
 * @endpoint GET /events/log/{event_id}
 */
async ({ eventId }: { eventId?: string } = {}): Promise<EventResponse> => {
        const response = await axios.get(`/events/log/${eventId}`)
        return ApiResponse.create(response.data, response)},
    get: /**
 * Get Event Log
 *
 * Get event log with filtering.
 * 
 * Shows all events that have been triggered, with optional filters:
 * - event_name: Filter by specific event
 * - source_module: Filter by module that emitted
 *
 * @endpoint GET /events/log
 */
async ({ _eventName, _sourceModule, limit = 50, offset }: { _eventName?: string, _sourceModule?: string, limit?: number, offset?: number } = {}): Promise<ApiResponse<EventResponse[]>> => {
        const response = await axios.get('/events/log', { params: {'event_name': _eventName, 'source_module': _sourceModule, limit, offset} })
        return ApiResponse.create(response.data, response)}
  },
  results: /**
 * Get Event Results
 *
 * Get handler results for an event.
 * 
 * Returns current state of all handler executions.
 * Client is responsible for retries/polling if needed.
 * 
 * Response:
 * {
 *   event_id: "uuid",
 *   complete: true,  // true if all handlers finished (success or failed)
 *   handlers: [
 *     {
 *       handler_name: "on_form_submission",
 *       status: "success",
 *       result: {person_id: "uuid", exam_id: "uuid"}
 *     }
 *   ]
 * }
 *
 * @endpoint GET /events/{event_id}/results
 */
async ({ eventId }: { eventId?: string } = {}): Promise<{ [key: string]: any }> => {
        const response = await axios.get(`/events/${eventId}/results`)
        return ApiResponse.create(response.data, response)},
  trigger: /**
 * Trigger Event
 *
 * Manually trigger an event (for testing).
 * 
 * Validates the payload against the registered event schema before emitting.
 * Context is automatically generated.
 * 
 * Example:
 *     POST /events/trigger/crawler.site_created
 *     {id: "123", name: "Test Site", base_url: "https://test.com"}
 *
 * @endpoint POST /events/trigger/{event_name}
 */
async ({ eventName, payload }: { eventName?: string, payload?: { [key: string]: any } } = {}): Promise<{ [key: string]: any }> => {
        const response = await axios.post(`/events/trigger/${eventName}`, payload)
        return ApiResponse.create(response.data, response)}
};

/**
 * API functions for person endpoints
 */
export const person = {
  getByPersonId: /**
 * Get Person
 *
 * Get a person by ID
 *
 * @endpoint GET /person/{person_id}
 */
async ({ personId }: { personId?: string } = {}): Promise<PersonWithRelations> => {
        const response = await axios.get(`/person/${personId}`)
        return ApiResponse.create(response.data, response)},
  put: /**
 * Update Person
 *
 * Update a person's SCALAR details only.
 * 
 * Nested emails/phones/addresses are IGNORED here — contacts are independent
 * sub-resources mutated via the granular /person/{id}/{email,phone} endpoints.
 * Nesting is only unambiguous on create; on update it caused the
 * delete-then-PUT-re-creates-with-new-id bug. We strip them and log a
 * deprecation warning (backward-compat). The response still returns current
 * contacts so the client can re-sync.
 *
 * @endpoint PUT /person/{person_id}
 */
async ({ personId, personWrite }: { personId?: string, personWrite?: PersonWrite } = {}): Promise<PersonWithRelations> => {
        const response = await axios.put(`/person/${personId}`, personWrite)
        return ApiResponse.create(response.data, response)},
  delete: /**
 * Delete Person
 *
 * Delete a person
 *
 * @endpoint DELETE /person/{person_id}
 */
async ({ personId }: { personId?: string } = {}): Promise<{ [key: string]: any }> => {
        const response = await axios.delete(`/person/${personId}`)
        return ApiResponse.create(response.data, response)},
  post: /**
 * Create New Person
 *
 * Create a person with full contact and address information
 *
 * @endpoint POST /person
 */
async ({ personWrite }: { personWrite?: PersonWrite } = {}): Promise<PersonWithRelations> => {
        const response = await axios.post('/person', personWrite)
        return ApiResponse.create(response.data, response)},
  get: /**
 * List Persons
 *
 * List/search persons with flexible filtering options.
 * 
 * Two search modes:
 * 
 * 1. Fuzzy Search (search parameter):
 *    - GET /person?search=john
 *    - Searches across all fields with relevance scoring
 *    - User-friendly for quick lookups
 * 
 * 2. Structured Filter (q parameter):
 *    - GET /person?q=first_name eq "John" and age gt 25
 *    - Precise SCIM 2.0 queries for any field
 *    - Supports relationships: emails.value, phones.value
 *    - Supports JSON arrays: roles has "Student"
 * 
 * Examples:
 *    - GET /person?search=john (fuzzy search)
 *    - GET /person?q=first_name eq "John" (structured filter)
 *    - GET /person?q=national_id eq "123456789" (find by national ID)
 *    - GET /person?q=roles has "Student" (filter by role - JSON array!)
 *    - GET /person?q=roles has "Student" and age gt 18 (combine filters)
 *    - GET /person?q=emails.value co "@example.com" (filter by email domain)
 *
 * @endpoint GET /person
 */
async ({ search, offset, limit = 100, _includeArchived, q }: { search?: string, offset?: number, limit?: number, _includeArchived?: boolean, q?: string } = {}): Promise<ApiResponse<PersonWithRelations[]>> => {
        const response = await axios.get('/person', { params: {search, offset, limit, 'include_archived': _includeArchived, q} })
        return ApiResponse.create(response.data, response)},
  findOrCreatePersonWithEmail: /**
 * Find Or Create Person With Email
 *
 * Create a new person
 *
 * @endpoint POST /person/find_or_create_person_with_email
 */
async ({ personWithEmail }: { personWithEmail?: PersonWithEmail } = {}): Promise<PersonWithRelations> => {
        const response = await axios.post('/person/find_or_create_person_with_email', personWithEmail)
        return ApiResponse.create(response.data, response)},
  findDuplicates: /**
 * Find Duplicates
 *
 * Find potential duplicate persons based on:
 * - Same email addresses
 * - Same phone numbers
 * - Similar names (exact first+last name match)
 * - Same national ID
 * 
 * Returns groups of duplicates with the oldest person as the primary.
 *
 * @endpoint GET /person/find-duplicates
 */
async ({ offset, limit = 100 }: { offset?: number, limit?: number } = {}): Promise<ApiResponse<DuplicateGroup[]>> => {
        const response = await axios.get('/person/find-duplicates', { params: {offset, limit} })
        return ApiResponse.create(response.data, response)},
  merge: /**
 * Merge Persons Route
 *
 * Merge duplicate persons into a primary person.
 * 
 * This endpoint:
 * - Updates all foreign key references from duplicates to primary
 * - Transfers all emails, phones, and addresses from duplicates to primary
 * - Merges roles from all persons
 * - Archives duplicate persons and marks them with merged_into_id
 * 
 * Note: Duplicates are archived rather than deleted to preserve audit trails.
 *
 * @endpoint POST /person/merge
 */
async ({ mergePersonsRequest }: { mergePersonsRequest?: MergePersonsRequest } = {}): Promise<PersonWithRelations> => {
        const response = await axios.post('/person/merge', mergePersonsRequest)
        return ApiResponse.create(response.data, response)},
  archive: /**
 * Archive Person
 *
 * Archive a person
 *
 * @endpoint PUT /person/{person_id}/archive
 */
async ({ personId }: { personId?: string } = {}): Promise<PersonWithRelations> => {
        const response = await axios.put(`/person/${personId}/archive`, {})
        return ApiResponse.create(response.data, response)},
  unarchive: /**
 * Unarchive Person
 *
 * Unarchive a person
 *
 * @endpoint PUT /person/{person_id}/unarchive
 */
async ({ personId }: { personId?: string } = {}): Promise<PersonWithRelations> => {
        const response = await axios.put(`/person/${personId}/unarchive`, {})
        return ApiResponse.create(response.data, response)},
  email: {
    patch: /**
 * Update Email
 *
 * Patch an email. Changing the value resets verification.
 *
 * @endpoint PATCH /person/email/{email_id}
 */
async ({ emailId, emailUpdate }: { emailId?: string, emailUpdate?: EmailUpdate } = {}): Promise<EmailRead> => {
        const response = await axios.patch(`/person/email/${emailId}`, emailUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Email
 *
 * Delete an email. Idempotent — deleting an already-gone id still 204s.
 *
 * @endpoint DELETE /person/email/{email_id}
 */
async ({ emailId }: { emailId?: string } = {}) => {
        const response = await axios.delete(`/person/email/${emailId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Email
 *
 * Create an email for a person (404 if the person doesn't exist).
 *
 * @endpoint POST /person/{person_id}/email
 */
async ({ personId, emailCreate }: { personId?: string, emailCreate?: EmailCreate } = {}): Promise<EmailRead> => {
        const response = await axios.post(`/person/${personId}/email`, emailCreate)
        return ApiResponse.create(response.data, response)},
    primary: /**
 * Set Primary Email Route
 *
 * Mark an email primary (unsets the person's other primary emails).
 *
 * @endpoint POST /person/email/{email_id}/primary
 */
async ({ emailId }: { emailId?: string } = {}): Promise<EmailRead> => {
        const response = await axios.post(`/person/email/${emailId}/primary`, {})
        return ApiResponse.create(response.data, response)},
    verify: /**
 * Verify Email
 *
 * Mark an email as verified
 *
 * @endpoint PUT /person/email/{email_id}/verify
 */
async ({ emailId }: { emailId?: string } = {}): Promise<EmailRead> => {
        const response = await axios.put(`/person/email/${emailId}/verify`, {})
        return ApiResponse.create(response.data, response)}
  },
  phone: {
    patch: /**
 * Update Phone
 *
 * Patch a phone. Changing the value resets verification.
 *
 * @endpoint PATCH /person/phone/{phone_id}
 */
async ({ phoneId, phoneUpdate }: { phoneId?: string, phoneUpdate?: PhoneUpdate } = {}): Promise<PhoneRead> => {
        const response = await axios.patch(`/person/phone/${phoneId}`, phoneUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Phone
 *
 * Delete a phone. Idempotent — deleting an already-gone id still 204s.
 *
 * @endpoint DELETE /person/phone/{phone_id}
 */
async ({ phoneId }: { phoneId?: string } = {}) => {
        const response = await axios.delete(`/person/phone/${phoneId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Phone
 *
 * Create a phone for a person (404 if the person doesn't exist).
 *
 * @endpoint POST /person/{person_id}/phone
 */
async ({ personId, phoneCreate }: { personId?: string, phoneCreate?: PhoneCreate } = {}): Promise<PhoneRead> => {
        const response = await axios.post(`/person/${personId}/phone`, phoneCreate)
        return ApiResponse.create(response.data, response)},
    primary: /**
 * Set Primary Phone Route
 *
 * Mark a phone primary (unsets the person's other primary phones).
 *
 * @endpoint POST /person/phone/{phone_id}/primary
 */
async ({ phoneId }: { phoneId?: string } = {}): Promise<PhoneRead> => {
        const response = await axios.post(`/person/phone/${phoneId}/primary`, {})
        return ApiResponse.create(response.data, response)},
    verify: /**
 * Verify Phone
 *
 * Mark a phone as verified
 *
 * @endpoint PUT /person/phone/{phone_id}/verify
 */
async ({ phoneId }: { phoneId?: string } = {}): Promise<PhoneRead> => {
        const response = await axios.put(`/person/phone/${phoneId}/verify`, {})
        return ApiResponse.create(response.data, response)}
  },
  address: {
    put: /**
 * Update Address Route
 *
 * Update an address
 *
 * @endpoint PUT /person/address/{address_id}
 */
async ({ addressId, addressWrite }: { addressId?: string, addressWrite?: AddressWrite } = {}): Promise<Address> => {
        const response = await axios.put(`/person/address/${addressId}`, addressWrite)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Address
 *
 * @endpoint DELETE /person/address/{address_id}
 */
async ({ addressId }: { addressId?: string } = {}): Promise<{ [key: string]: any }> => {
        const response = await axios.delete(`/person/address/${addressId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Add Address
 *
 * Add an address to a person
 *
 * @endpoint POST /person/{person_id}/address
 */
async ({ personId, address }: { personId?: string, address?: Address } = {}): Promise<Address> => {
        const response = await axios.post(`/person/${personId}/address`, address)
        return ApiResponse.create(response.data, response)}
  }
};

/**
 * API functions for authentication endpoints
 */
export const authentication = {
  status: /**
 * Auth Status
 *
 * Get authentication status and available methods
 *
 * @endpoint GET /authentication/status
 */
async (): Promise<AuthStatusResponse> => {
        const response = await axios.get('/authentication/status')
        return ApiResponse.create(response.data, response)},
  methods: /**
 * Get Available Methods
 *
 * Get available authentication methods for this instance
 *
 * @endpoint GET /authentication/methods
 */
async (): Promise<AvailableMethodsResponse> => {
        const response = await axios.get('/authentication/methods')
        return ApiResponse.create(response.data, response)},
  register: /**
 * Register
 *
 * Register a new identity with authentication methods
 *
 * @endpoint POST /authentication/register
 */
async ({ registerRequest }: { registerRequest?: RegisterRequest } = {}): Promise<AuthenticationResponse> => {
        const response = await axios.post('/authentication/register', registerRequest)
        return ApiResponse.create(response.data, response)},
  logout: /**
 * Logout
 *
 * Logout and clear session
 *
 * @endpoint POST /authentication/logout
 */
async (): Promise<MessageResponse> => {
        const response = await axios.post('/authentication/logout', {})
        return ApiResponse.create(response.data, response)},
  sessions: {
    deleteBySessionToken: /**
 * Revoke Session
 *
 * Revoke a specific session
 *
 * @endpoint DELETE /authentication/sessions/{session_token}
 */
async ({ sessionToken }: { sessionToken?: string } = {}): Promise<MessageResponse> => {
        const response = await axios.delete(`/authentication/sessions/${sessionToken}`)
        return ApiResponse.create(response.data, response)},
    get: /**
 * Get My Sessions
 *
 * Get active sessions for current identity
 *
 * @endpoint GET /authentication/sessions
 */
async (): Promise<SessionListResponse> => {
        const response = await axios.get('/authentication/sessions')
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Revoke All My Sessions
 *
 * Revoke all sessions for current identity
 *
 * @endpoint DELETE /authentication/sessions
 */
async (): Promise<MessageResponse> => {
        const response = await axios.delete('/authentication/sessions')
        return ApiResponse.create(response.data, response)}
  },
  cleanupSessions: /**
 * Cleanup Expired Sessions
 *
 * Clean up expired sessions (admin endpoint)
 *
 * @endpoint POST /authentication/cleanup-sessions
 */
async (): Promise<MessageResponse> => {
        const response = await axios.post('/authentication/cleanup-sessions', {})
        return ApiResponse.create(response.data, response)},
  me: {
    get: /**
 * Get Current User
 *
 * Get current authenticated identity info
 *
 * @endpoint GET /authentication/me
 */
async (): Promise<AccountInfo> => {
        const response = await axios.get('/authentication/me')
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Current User
 *
 * Delete current identity (soft delete - deactivates)
 *
 * @endpoint DELETE /authentication/me
 */
async (): Promise<MessageResponse> => {
        const response = await axios.delete('/authentication/me')
        return ApiResponse.create(response.data, response)},
    patch: /**
 * Update Current User
 *
 * Update current identity's display name
 *
 * @endpoint PATCH /authentication/me
 */
async ({ updateAccountRequest }: { updateAccountRequest?: UpdateAccountRequest } = {}): Promise<AccountInfo> => {
        const response = await axios.patch('/authentication/me', updateAccountRequest)
        return ApiResponse.create(response.data, response)}
  },
  password: {
    change: /**
 * Change Password
 *
 * Change password for current user (requires current password)
 *
 * @endpoint POST /authentication/password/change
 */
async ({ changePasswordRequest }: { changePasswordRequest?: ChangePasswordRequest } = {}): Promise<MessageResponse> => {
        const response = await axios.post('/authentication/password/change', changePasswordRequest)
        return ApiResponse.create(response.data, response)},
    forgot: /**
 * Forgot Password
 *
 * Initiate password reset flow
 *
 * @endpoint POST /authentication/password/forgot
 */
async ({ forgotPasswordRequest }: { forgotPasswordRequest?: ForgotPasswordRequest } = {}): Promise<MessageResponse> => {
        const response = await axios.post('/authentication/password/forgot', forgotPasswordRequest)
        return ApiResponse.create(response.data, response)},
    verifyResetToken: /**
 * Verify Reset Token
 *
 * Verify if password reset token is valid
 *
 * @endpoint GET /authentication/password/verify-reset-token/{token}
 */
async ({ token }: { token?: string } = {}): Promise<MessageResponse> => {
        const response = await axios.get(`/authentication/password/verify-reset-token/${token}`)
        return ApiResponse.create(response.data, response)},
    reset: /**
 * Reset Password
 *
 * Reset password with token
 *
 * @endpoint POST /authentication/password/reset
 */
async ({ resetPasswordRequest }: { resetPasswordRequest?: ResetPasswordRequest } = {}): Promise<MessageResponse> => {
        const response = await axios.post('/authentication/password/reset', resetPasswordRequest)
        return ApiResponse.create(response.data, response)}
  },
  verify: {
    send: /**
 * Send Email Verification
 *
 * Send email verification link
 *
 * @endpoint POST /authentication/verify/send
 */
async ({ identity, sendVerificationRequest }: { identity?: any, sendVerificationRequest?: SendVerificationRequest } = {}): Promise<MessageResponse> => {
        const response = await axios.post('/authentication/verify/send', sendVerificationRequest, { params: {identity} })
        return ApiResponse.create(response.data, response)},
    email: /**
 * Verify Email
 *
 * Verify email with token
 *
 * @endpoint POST /authentication/verify/email
 */
async ({ verifyEmailRequest }: { verifyEmailRequest?: VerifyEmailRequest } = {}): Promise<MessageResponse> => {
        const response = await axios.post('/authentication/verify/email', verifyEmailRequest)
        return ApiResponse.create(response.data, response)}
  },
  refresh: /**
 * Refresh Session
 *
 * Refresh current session and extend expiration
 *
 * @endpoint POST /authentication/refresh
 */
async (): Promise<AuthenticationResponse> => {
        const response = await axios.post('/authentication/refresh', {})
        return ApiResponse.create(response.data, response)},
  login: {
    password: /**
 * Login Password
 *
 * Login with email and password
 *
 * @endpoint POST /authentication/login/password
 */
async ({ passwordLoginRequest }: { passwordLoginRequest?: PasswordLoginRequest } = {}): Promise<AuthenticationResponse> => {
        const response = await axios.post('/authentication/login/password', passwordLoginRequest)
        return ApiResponse.create(response.data, response)},
    sso: {
      google: /**
 * Login Sso
 *
 * Login with this SSO provider (legacy endpoint without PKCE)
 *
 * @endpoint POST /authentication/login/sso/google
 */
async ({ sSOLoginRequest }: { sSOLoginRequest?: SSOLoginRequest } = {}): Promise<AuthenticationResponse> => {
        const response = await axios.post('/authentication/login/sso/google', sSOLoginRequest)
        return ApiResponse.create(response.data, response)}
    }
  },
  sso: {
    google: {
      initiate: /**
 * Initiate Sso
 *
 * Initiate SSO flow with PKCE
 *
 * @endpoint POST /authentication/sso/google/initiate
 */
async ({ sSOInitiateRequest }: { sSOInitiateRequest?: SSOInitiateRequest } = {}): Promise<SSOInitiateResponse> => {
        const response = await axios.post('/authentication/sso/google/initiate', sSOInitiateRequest)
        return ApiResponse.create(response.data, response)},
      callback: /**
 * Sso Callback
 *
 * Handle SSO callback with PKCE verification
 *
 * @endpoint POST /authentication/sso/google/callback
 */
async ({ sSOCallbackRequest }: { sSOCallbackRequest?: SSOCallbackRequest } = {}): Promise<SSOCallbackResponse> => {
        const response = await axios.post('/authentication/sso/google/callback', sSOCallbackRequest)
        return ApiResponse.create(response.data, response)},
      link: /**
 * Link Sso Provider
 *
 * Link SSO provider to existing account
 *
 * @endpoint POST /authentication/sso/google/link
 */
async ({ sSOLinkRequest }: { sSOLinkRequest?: SSOLinkRequest } = {}): Promise<SSOLinkResponse> => {
        const response = await axios.post('/authentication/sso/google/link', sSOLinkRequest)
        return ApiResponse.create(response.data, response)},
      unlink: /**
 * Unlink Sso Provider
 *
 * Unlink SSO provider from account
 *
 * @endpoint DELETE /authentication/sso/google/unlink
 */
async (): Promise<SSOUnlinkResponse> => {
        const response = await axios.delete('/authentication/sso/google/unlink')
        return ApiResponse.create(response.data, response)}
    }
  }
};

/**
 * API functions for admin endpoints
 */
export const admin = {
  identities: {
    getByIdentityId: /**
 * Get Identity
 *
 * Get a single identity with their linked person.
 *
 * @endpoint GET /admin/identities/{identity_id}
 */
async ({ identityId }: { identityId?: string } = {}): Promise<AccountInfo> => {
        const response = await axios.get(`/admin/identities/${identityId}`)
        return ApiResponse.create(response.data, response)},
    patch: /**
 * Update Identity
 *
 * Update roles, active status, or unlock an identity.
 *
 * @endpoint PATCH /admin/identities/{identity_id}
 */
async ({ identityId, identityAdminUpdate }: { identityId?: string, identityAdminUpdate?: IdentityAdminUpdate } = {}): Promise<AccountInfo> => {
        const response = await axios.patch(`/admin/identities/${identityId}`, identityAdminUpdate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Identities
 *
 * List all identities with their linked person.
 * 
 * When ``_tenantId`` is provided, only identities with an active
 * TenantMembership in that tenant are returned.
 * 
 * Supports ``?search=`` for free-text keyword search across email,
 * display name, and person name, and ``?q=`` for SCIM 2.0 query filters
 * on Identity fields.
 *
 * @endpoint GET /admin/identities
 */
async ({ offset, limit = 100, _includeInactive, _tenantId, search, q }: { offset?: number, limit?: number, _includeInactive?: boolean, _tenantId?: string, search?: string, q?: string } = {}): Promise<ApiResponse<AccountInfo[]>> => {
        const response = await axios.get('/admin/identities', { params: {offset, limit, 'include_inactive': _includeInactive, '_tenantId': _tenantId, search, q} })
        return ApiResponse.create(response.data, response)}
  }
};

/**
 * API functions for blox endpoints
 */
export const blox = {
  websites: {
    getByWebsiteId: /**
 * Get Website Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}
 */
async ({ websiteId, locale }: { websiteId?: string, locale?: string } = {}): Promise<WebsiteRead> => {
        const response = await axios.get(`/blox/websites/${websiteId}`, { params: {locale} })
        return ApiResponse.create(response.data, response)},
    put: /**
 * Update Website Endpoint
 *
 * @endpoint PUT /blox/websites/{website_id}
 */
async ({ websiteId, websiteUpdate }: { websiteId?: string, websiteUpdate?: WebsiteUpdate } = {}): Promise<WebsiteRead> => {
        const response = await axios.put(`/blox/websites/${websiteId}`, websiteUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Website Endpoint
 *
 * @endpoint DELETE /blox/websites/{website_id}
 */
async ({ websiteId }: { websiteId?: string } = {}) => {
        const response = await axios.delete(`/blox/websites/${websiteId}`)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Websites Endpoint
 *
 * @endpoint GET /blox/websites
 */
async (): Promise<ApiResponse<WebsiteRead[]>> => {
        const response = await axios.get('/blox/websites')
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Website Endpoint
 *
 * @endpoint POST /blox/websites
 */
async ({ websiteCreate }: { websiteCreate?: WebsiteCreate } = {}): Promise<WebsiteRead> => {
        const response = await axios.post('/blox/websites', websiteCreate)
        return ApiResponse.create(response.data, response)},
    manifest: {
      put: /**
 * Set Manifest Endpoint
 *
 * Sync the block manifest from the deployed frontend template.
 * 
 * Called at build/deploy time (or on GitHub import) with the contents of the
 * site's generated `.blox-manifest.json`. Powers the agent's `describe_blocks`
 * tool and server-side page validation.
 *
 * @endpoint PUT /blox/websites/{website_id}/manifest
 */
async ({ websiteId, blockManifest }: { websiteId?: string, blockManifest?: BlockManifest } = {}): Promise<WebsiteRead> => {
        const response = await axios.put(`/blox/websites/${websiteId}/manifest`, blockManifest)
        return ApiResponse.create(response.data, response)},
      get: /**
 * Get Manifest Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/manifest
 */
async ({ websiteId }: { websiteId?: string } = {}): Promise<BlockManifest> => {
        const response = await axios.get(`/blox/websites/${websiteId}/manifest`)
        return ApiResponse.create(response.data, response)}
    },
    pages: {
      getByPageId: /**
 * Get Page Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/pages/{page_id}
 */
async ({ websiteId, pageId, locale }: { websiteId?: string, pageId?: string, locale?: string } = {}): Promise<PageRead> => {
        const response = await axios.get(`/blox/websites/${websiteId}/pages/${pageId}`, { params: {locale} })
        return ApiResponse.create(response.data, response)},
      put: /**
 * Update Page Endpoint
 *
 * @endpoint PUT /blox/websites/{website_id}/pages/{page_id}
 */
async ({ websiteId, pageId, locale, pageUpdate }: { websiteId?: string, pageId?: string, locale?: string, pageUpdate?: PageUpdate } = {}): Promise<PageRead> => {
        const response = await axios.put(`/blox/websites/${websiteId}/pages/${pageId}`, pageUpdate, { params: {locale} })
        return ApiResponse.create(response.data, response)},
      delete: /**
 * Delete Page Endpoint
 *
 * @endpoint DELETE /blox/websites/{website_id}/pages/{page_id}
 */
async ({ websiteId, pageId }: { websiteId?: string, pageId?: string } = {}) => {
        const response = await axios.delete(`/blox/websites/${websiteId}/pages/${pageId}`)
        return ApiResponse.create(response.data, response)},
      post: /**
 * Create Page Endpoint
 *
 * @endpoint POST /blox/websites/{website_id}/pages
 */
async ({ websiteId, pageCreate }: { websiteId?: string, pageCreate?: PageCreate } = {}): Promise<PageRead> => {
        const response = await axios.post(`/blox/websites/${websiteId}/pages`, pageCreate)
        return ApiResponse.create(response.data, response)},
      get: /**
 * List Pages Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/pages
 */
async ({ websiteId, locale }: { websiteId?: string, locale?: string } = {}): Promise<ApiResponse<PageRead[]>> => {
        const response = await axios.get(`/blox/websites/${websiteId}/pages`, { params: {locale} })
        return ApiResponse.create(response.data, response)},
      bySlug: /**
 * Get Page By Slug Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/pages/by-slug
 */
async ({ websiteId, slug, locale }: { websiteId?: string, slug?: string, locale?: string } = {}): Promise<PageRead> => {
        const response = await axios.get(`/blox/websites/${websiteId}/pages/by-slug`, { params: {slug, locale} })
        return ApiResponse.create(response.data, response)},
      move: /**
 * Move Page Endpoint
 *
 * @endpoint PATCH /blox/websites/{website_id}/pages/{page_id}/move
 */
async ({ websiteId, pageId, moveRequest }: { websiteId?: string, pageId?: string, moveRequest?: MoveRequest } = {}): Promise<PageRead> => {
        const response = await axios.patch(`/blox/websites/${websiteId}/pages/${pageId}/move`, moveRequest)
        return ApiResponse.create(response.data, response)},
      versions: {
        get: /**
 * List Versions Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/pages/{page_id}/versions
 */
async ({ websiteId, pageId }: { websiteId?: string, pageId?: string } = {}): Promise<ApiResponse<PageVersionRead[]>> => {
        const response = await axios.get(`/blox/websites/${websiteId}/pages/${pageId}/versions`)
        return ApiResponse.create(response.data, response)},
        restore: /**
 * Restore Version Endpoint
 *
 * @endpoint POST /blox/websites/{website_id}/pages/{page_id}/versions/{version_id}/restore
 */
async ({ websiteId, pageId, versionId }: { websiteId?: string, pageId?: string, versionId?: string } = {}): Promise<PageRead> => {
        const response = await axios.post(`/blox/websites/${websiteId}/pages/${pageId}/versions/${versionId}/restore`, {})
        return ApiResponse.create(response.data, response)}
      },
      publish: /**
 * Publish Page Endpoint
 *
 * Publish a single page (promote its draft), then fire publish hooks.
 *
 * @endpoint POST /blox/websites/{website_id}/pages/{page_id}/publish
 */
async ({ websiteId, pageId }: { websiteId?: string, pageId?: string } = {}): Promise<PublishResult> => {
        const response = await axios.post(`/blox/websites/${websiteId}/pages/${pageId}/publish`, {})
        return ApiResponse.create(response.data, response)},
      schema: /**
 * Page Schema Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/pages/{page_id}/schema
 */
async ({ websiteId, pageId }: { websiteId?: string, pageId?: string } = {}): Promise<PageSchemaRead> => {
        const response = await axios.get(`/blox/websites/${websiteId}/pages/${pageId}/schema`)
        return ApiResponse.create(response.data, response)},
      resolve: /**
 * Page Resolve Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/pages/{page_id}/resolve
 */
async ({ websiteId, pageId, locale }: { websiteId?: string, pageId?: string, locale?: string } = {}): Promise<PageResolveRead> => {
        const response = await axios.get(`/blox/websites/${websiteId}/pages/${pageId}/resolve`, { params: {locale} })
        return ApiResponse.create(response.data, response)}
    },
    publish: /**
 * Publish Website Endpoint
 *
 * @endpoint POST /blox/websites/{website_id}/publish
 */
async ({ websiteId }: { websiteId?: string } = {}): Promise<PublishResult> => {
        const response = await axios.post(`/blox/websites/${websiteId}/publish`, {})
        return ApiResponse.create(response.data, response)},
    globalBlocks: {
      put: /**
 * Upsert Global Block Endpoint
 *
 * Upsert one global block's props. `?locale=xx` stores a translation (§D.5).
 *
 * @endpoint PUT /blox/websites/{website_id}/global-blocks/{block_type}
 */
async ({ websiteId, blockType, locale, globalBlockUpsert }: { websiteId?: string, blockType?: string, locale?: string, globalBlockUpsert?: GlobalBlockUpsert } = {}): Promise<GlobalBlockRead> => {
        const response = await axios.put(`/blox/websites/${websiteId}/global-blocks/${blockType}`, globalBlockUpsert, { params: {locale} })
        return ApiResponse.create(response.data, response)},
      delete: /**
 * Delete Global Block Endpoint
 *
 * @endpoint DELETE /blox/websites/{website_id}/global-blocks/{block_type}
 */
async ({ websiteId, blockType }: { websiteId?: string, blockType?: string } = {}) => {
        const response = await axios.delete(`/blox/websites/${websiteId}/global-blocks/${blockType}`)
        return ApiResponse.create(response.data, response)},
      get: /**
 * List Global Blocks Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/global-blocks
 */
async ({ websiteId, locale }: { websiteId?: string, locale?: string } = {}): Promise<ApiResponse<GlobalBlockRead[]>> => {
        const response = await axios.get(`/blox/websites/${websiteId}/global-blocks`, { params: {locale} })
        return ApiResponse.create(response.data, response)}
    },
    resolvePath: /**
 * Resolve Path Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/resolve-path
 */
async ({ websiteId, path, locale }: { websiteId?: string, path?: string, locale?: string } = {}): Promise<PageResolveRead> => {
        const response = await axios.get(`/blox/websites/${websiteId}/resolve-path`, { params: {path, locale} })
        return ApiResponse.create(response.data, response)},
    redirects: {
      put: /**
 * Update Redirect Endpoint
 *
 * @endpoint PUT /blox/websites/{website_id}/redirects/{redirect_id}
 */
async ({ websiteId, redirectId, redirectUpdate }: { websiteId?: string, redirectId?: string, redirectUpdate?: RedirectUpdate } = {}): Promise<RedirectRead> => {
        const response = await axios.put(`/blox/websites/${websiteId}/redirects/${redirectId}`, redirectUpdate)
        return ApiResponse.create(response.data, response)},
      delete: /**
 * Delete Redirect Endpoint
 *
 * @endpoint DELETE /blox/websites/{website_id}/redirects/{redirect_id}
 */
async ({ websiteId, redirectId }: { websiteId?: string, redirectId?: string } = {}) => {
        const response = await axios.delete(`/blox/websites/${websiteId}/redirects/${redirectId}`)
        return ApiResponse.create(response.data, response)},
      post: /**
 * Create Redirect Endpoint
 *
 * @endpoint POST /blox/websites/{website_id}/redirects
 */
async ({ websiteId, redirectCreate }: { websiteId?: string, redirectCreate?: RedirectCreate } = {}): Promise<RedirectRead> => {
        const response = await axios.post(`/blox/websites/${websiteId}/redirects`, redirectCreate)
        return ApiResponse.create(response.data, response)},
      get: /**
 * List Redirects Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/redirects
 */
async ({ websiteId, offset, limit = 100 }: { websiteId?: string, offset?: number, limit?: number } = {}): Promise<ApiResponse<RedirectRead[]>> => {
        const response = await axios.get(`/blox/websites/${websiteId}/redirects`, { params: {offset, limit} })
        return ApiResponse.create(response.data, response)}
    },
    robotsTxt: /**
 * Get Robots Txt Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/robots.txt
 */
async ({ websiteId }: { websiteId?: string } = {}) => {
        const response = await axios.get(`/blox/websites/${websiteId}/robots.txt`)
        return ApiResponse.create(response.data, response)},
    analytics: {
      site: {
        post: /**
 * Create Analytics Site Endpoint
 *
 * @endpoint POST /blox/websites/{website_id}/analytics/site
 */
async ({ websiteId, plausibleSiteCreate }: { websiteId?: string, plausibleSiteCreate?: PlausibleSiteCreate } = {}): Promise<PlausibleSiteRead> => {
        const response = await axios.post(`/blox/websites/${websiteId}/analytics/site`, plausibleSiteCreate)
        return ApiResponse.create(response.data, response)},
        get: /**
 * Get Analytics Site Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/analytics/site
 */
async ({ websiteId }: { websiteId?: string } = {}): Promise<PlausibleSiteRead> => {
        const response = await axios.get(`/blox/websites/${websiteId}/analytics/site`)
        return ApiResponse.create(response.data, response)},
        delete: /**
 * Delete Analytics Site Endpoint
 *
 * @endpoint DELETE /blox/websites/{website_id}/analytics/site
 */
async ({ websiteId }: { websiteId?: string } = {}) => {
        const response = await axios.delete(`/blox/websites/${websiteId}/analytics/site`)
        return ApiResponse.create(response.data, response)}
      },
      stats: {
        realtime: /**
 * Analytics Realtime Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/analytics/stats/realtime
 */
async ({ websiteId }: { websiteId?: string } = {}): Promise<PlausibleRealtimeVisitors> => {
        const response = await axios.get(`/blox/websites/${websiteId}/analytics/stats/realtime`)
        return ApiResponse.create(response.data, response)},
        aggregate: /**
 * Analytics Aggregate Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/analytics/stats/aggregate
 */
async ({ websiteId, period = '30d', date, metrics, filters }: { websiteId?: string, period?: string, date?: string, metrics?: string, filters?: string } = {}): Promise<PlausibleAggregateMetrics> => {
        const response = await axios.get(`/blox/websites/${websiteId}/analytics/stats/aggregate`, { params: {period, date, metrics, filters} })
        return ApiResponse.create(response.data, response)},
        timeseries: /**
 * Analytics Timeseries Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/analytics/stats/timeseries
 */
async ({ websiteId, period = '30d', date, metrics, filters }: { websiteId?: string, period?: string, date?: string, metrics?: string, filters?: string } = {}): Promise<PlausibleTimeseriesRead> => {
        const response = await axios.get(`/blox/websites/${websiteId}/analytics/stats/timeseries`, { params: {period, date, metrics, filters} })
        return ApiResponse.create(response.data, response)},
        breakdown: /**
 * Analytics Breakdown Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/analytics/stats/breakdown
 */
async ({ websiteId, property, period = '30d', date, metrics, filters, limit = 100, page = 1 }: { websiteId?: string, property?: string, period?: string, date?: string, metrics?: string, filters?: string, limit?: number, page?: number } = {}): Promise<PlausibleBreakdownRead> => {
        const response = await axios.get(`/blox/websites/${websiteId}/analytics/stats/breakdown`, { params: {property, period, date, metrics, filters, limit, page} })
        return ApiResponse.create(response.data, response)}
      },
      goals: {
        delete: /**
 * Analytics Delete Goal Endpoint
 *
 * @endpoint DELETE /blox/websites/{website_id}/analytics/goals/{goal_id}
 */
async ({ websiteId, goalId }: { websiteId?: string, goalId?: string } = {}) => {
        const response = await axios.delete(`/blox/websites/${websiteId}/analytics/goals/${goalId}`)
        return ApiResponse.create(response.data, response)},
        get: /**
 * Analytics List Goals Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/analytics/goals
 */
async ({ websiteId }: { websiteId?: string } = {}): Promise<PlausibleGoalsList> => {
        const response = await axios.get(`/blox/websites/${websiteId}/analytics/goals`)
        return ApiResponse.create(response.data, response)},
        post: /**
 * Analytics Create Goal Endpoint
 *
 * @endpoint POST /blox/websites/{website_id}/analytics/goals
 */
async ({ websiteId, plausibleGoalCreate }: { websiteId?: string, plausibleGoalCreate?: PlausibleGoalCreate } = {}): Promise<PlausibleGoalRead> => {
        const response = await axios.post(`/blox/websites/${websiteId}/analytics/goals`, plausibleGoalCreate)
        return ApiResponse.create(response.data, response)}
      },
      funnels: /**
 * Analytics List Funnels Endpoint
 *
 * @endpoint GET /blox/websites/{website_id}/analytics/funnels
 */
async ({ websiteId }: { websiteId?: string } = {}): Promise<PlausibleFunnelsList> => {
        const response = await axios.get(`/blox/websites/${websiteId}/analytics/funnels`)
        return ApiResponse.create(response.data, response)}
    }
  }
};

/**
 * API functions for datastore endpoints
 */
export const datastore = {
  schemas: {
    getByCollectionName: /**
 * Get Schema Endpoint
 *
 * @endpoint GET /datastore/{store}/schemas/{collection_name}
 */
async ({ store, collectionName }: { store?: string, collectionName?: string } = {}): Promise<CollectionSchemaRead> => {
        const response = await axios.get(`/datastore/${store}/schemas/${collectionName}`)
        return ApiResponse.create(response.data, response)},
    put: /**
 * Update Schema Endpoint
 *
 * @endpoint PUT /datastore/{store}/schemas/{collection_name}
 */
async ({ store, collectionName, collectionSchemaUpdate }: { store?: string, collectionName?: string, collectionSchemaUpdate?: CollectionSchemaUpdate } = {}): Promise<CollectionSchemaRead> => {
        const response = await axios.put(`/datastore/${store}/schemas/${collectionName}`, collectionSchemaUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Schema Endpoint
 *
 * @endpoint DELETE /datastore/{store}/schemas/{collection_name}
 */
async ({ store, collectionName }: { store?: string, collectionName?: string } = {}) => {
        const response = await axios.delete(`/datastore/${store}/schemas/${collectionName}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Schema Endpoint
 *
 * @endpoint POST /datastore/{store}/schemas
 */
async ({ store, collectionSchemaCreate }: { store?: string, collectionSchemaCreate?: CollectionSchemaCreate } = {}): Promise<CollectionSchemaRead> => {
        const response = await axios.post(`/datastore/${store}/schemas`, collectionSchemaCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Schemas Endpoint
 *
 * @endpoint GET /datastore/{store}/schemas
 */
async ({ store }: { store?: string } = {}): Promise<ApiResponse<CollectionSchemaRead[]>> => {
        const response = await axios.get(`/datastore/${store}/schemas`)
        return ApiResponse.create(response.data, response)}
  },
  collections: {
    post: /**
 * Create Collection Item Endpoint
 *
 * @endpoint POST /datastore/{store}/collections/{collection_name}
 */
async ({ store, collectionName, locale = 'en', collectionItemCreate }: { store?: string, collectionName?: string, locale?: string, collectionItemCreate?: CollectionItemCreate } = {}): Promise<CollectionItemRead> => {
        const response = await axios.post(`/datastore/${store}/collections/${collectionName}`, collectionItemCreate, { params: {locale} })
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Collection Items Endpoint
 *
 * @endpoint GET /datastore/{store}/collections/{collection_name}
 */
async ({ store, collectionName, search, q, include = [], select = [], offset, limit = 100, locale = 'en', _includeDeleted }: { store?: string, collectionName?: string, search?: string, q?: string, include?: string[], select?: string[], offset?: number, limit?: number, locale?: string, _includeDeleted?: boolean } = {}): Promise<ApiResponse<CollectionItemRead[]>> => {
        const response = await axios.get(`/datastore/${store}/collections/${collectionName}`, { params: {search, q, include, select, offset, limit, locale, 'include_deleted': _includeDeleted} })
        return ApiResponse.create(response.data, response)},
    item: {
      get: /**
 * Get Collection Item Endpoint
 *
 * @endpoint GET /datastore/{store}/collections/{collection_name}/item/{item_id}
 */
async ({ store, collectionName, itemId, include = [], select = [], locale = 'en' }: { store?: string, collectionName?: string, itemId?: string, include?: string[], select?: string[], locale?: string } = {}): Promise<CollectionItemRead> => {
        const response = await axios.get(`/datastore/${store}/collections/${collectionName}/item/${itemId}`, { params: {include, select, locale} })
        return ApiResponse.create(response.data, response)},
      put: /**
 * Update Collection Item Endpoint
 *
 * @endpoint PUT /datastore/{store}/collections/{collection_name}/item/{item_id}
 */
async ({ collectionName, store, itemId, locale = 'en', collectionItemUpdate }: { collectionName?: string, store?: string, itemId?: string, locale?: string, collectionItemUpdate?: CollectionItemUpdate } = {}): Promise<CollectionItemRead> => {
        const response = await axios.put(`/datastore/${store}/collections/${collectionName}/item/${itemId}`, collectionItemUpdate, { params: {locale} })
        return ApiResponse.create(response.data, response)},
      delete: /**
 * Delete Collection Item Endpoint
 *
 * @endpoint DELETE /datastore/{store}/collections/{collection_name}/item/{item_id}
 */
async ({ collectionName, store, itemId, force }: { collectionName?: string, store?: string, itemId?: string, force?: boolean } = {}) => {
        const response = await axios.delete(`/datastore/${store}/collections/${collectionName}/item/${itemId}`, { params: {force} })
        return ApiResponse.create(response.data, response)},
      locales: /**
 * Delete Item Locale Endpoint
 *
 * Remove one language from an item (its translations + availability).
 * 
 * Refuses to remove the last remaining locale (409) — delete the item instead.
 *
 * @endpoint DELETE /datastore/{store}/collections/{collection_name}/item/{item_id}/locales/{locale_code}
 */
async ({ collectionName, store, itemId, localeCode, locale = 'en' }: { collectionName?: string, store?: string, itemId?: string, localeCode?: string, locale?: string } = {}): Promise<CollectionItemRead> => {
        const response = await axios.delete(`/datastore/${store}/collections/${collectionName}/item/${itemId}/locales/${localeCode}`, { params: {locale} })
        return ApiResponse.create(response.data, response)},
      restore: /**
 * Restore Collection Item Endpoint
 *
 * @endpoint POST /datastore/{store}/collections/{collection_name}/item/{item_id}/restore
 */
async ({ collectionName, store, itemId, locale = 'en' }: { collectionName?: string, store?: string, itemId?: string, locale?: string } = {}): Promise<CollectionItemRead> => {
        const response = await axios.post(`/datastore/${store}/collections/${collectionName}/item/${itemId}/restore`, {}, { params: {locale} })
        return ApiResponse.create(response.data, response)},
      move: /**
 * Move Collection Item Endpoint
 *
 * @endpoint PATCH /datastore/{store}/collections/{collection_name}/item/{item_id}/move
 */
async ({ store, collectionName, itemId, moveRequest }: { store?: string, collectionName?: string, itemId?: string, moveRequest?: MoveRequest } = {}): Promise<CollectionItemRead> => {
        const response = await axios.patch(`/datastore/${store}/collections/${collectionName}/item/${itemId}/move`, moveRequest)
        return ApiResponse.create(response.data, response)}
    }
  }
};

/**
 * API functions for form endpoints
 */
export const form = {
  get: /**
 * List Form Definitions
 *
 * @endpoint GET /form/
 */
async ({ _activeOnly = true, offset, limit = 100 }: { _activeOnly?: boolean, offset?: number, limit?: number } = {}): Promise<ApiResponse<FormDefinitionRead[]>> => {
        const response = await axios.get('/form/', { params: {'active_only': _activeOnly, offset, limit} })
        return ApiResponse.create(response.data, response)},
  submit: {
    postByFormName: /**
 * Submit Form By Name
 *
 * Submit a form with form name from the URL path.
 * 
 * Body is 100 % business data; control fields go in ``_meta``.
 *
 * @endpoint POST /form/submit/{form_name}
 */
async ({ formName, formSubmissionCreate }: { formName?: string, formSubmissionCreate?: FormSubmissionCreate } = {}): Promise<FormSubmissionRead> => {
        const response = await axios.post(`/form/submit/${formName}`, formSubmissionCreate)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Submit Form
 *
 * Submit a form (form_name optional via ``_meta.form_name``).
 * 
 * Body is 100 % business data; control fields go in ``_meta``.
 * Analytics are extracted from query params and headers automatically.
 *
 * @endpoint POST /form/submit
 */
async ({ formSubmissionCreate }: { formSubmissionCreate?: FormSubmissionCreate } = {}): Promise<FormSubmissionRead> => {
        const response = await axios.post('/form/submit', formSubmissionCreate)
        return ApiResponse.create(response.data, response)}
  },
  submissions: {
    getBySubmissionId: /**
 * Get Submission
 *
 * Get a form submission by ID.
 *
 * @endpoint GET /form/submissions/{submission_id}
 */
async ({ submissionId }: { submissionId?: string } = {}): Promise<FormSubmissionRead> => {
        const response = await axios.get(`/form/submissions/${submissionId}`)
        return ApiResponse.create(response.data, response)},
    put: /**
 * Update Submission
 *
 * Update a form submission. Control fields in ``_meta``.
 *
 * @endpoint PUT /form/submissions/{submission_id}
 */
async ({ submissionId, formSubmissionUpdate }: { submissionId?: string, formSubmissionUpdate?: FormSubmissionUpdate } = {}): Promise<FormSubmissionRead> => {
        const response = await axios.put(`/form/submissions/${submissionId}`, formSubmissionUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Submission
 *
 * Delete a form submission by ID.
 *
 * @endpoint DELETE /form/submissions/{submission_id}
 */
async ({ submissionId }: { submissionId?: string } = {}) => {
        const response = await axios.delete(`/form/submissions/${submissionId}`)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Submissions
 *
 * List form submissions with pagination, SCIM filter, sort, and search.
 * 
 * - Pagination: ``offset`` / ``limit`` (headers carry total).
 * - Filter (``?q=``): SCIM 2.0. ``utm_source`` etc. are real columns; any other
 *   bare field (e.g. ``email``) is matched against the ``form_data`` JSONB body.
 * - Sort (``?sort=``): ``field:dir`` — form_data keys sortable too.
 * - Search (``?search=``): free text over form name + form_data body.
 *
 * @endpoint GET /form/submissions
 */
async ({ _formName, status, _personId, _fieldKey, _fieldValue, offset, limit = 100, q, sort, search }: { _formName?: string, status?: FormSubStatus, _personId?: string, _fieldKey?: string, _fieldValue?: string, offset?: number, limit?: number, q?: string, sort?: string, search?: string } = {}): Promise<ApiResponse<FormSubmissionRead[]>> => {
        const response = await axios.get('/form/submissions', { params: {'form_name': _formName, status, 'person_id': _personId, 'field_key': _fieldKey, 'field_value': _fieldValue, offset, limit, q, sort, search} })
        return ApiResponse.create(response.data, response)},
    step: /**
 * Submit Form Step
 *
 * Submit a step in a multi-step form.
 * 
 * ``_meta.form_step_name`` is required for this endpoint.
 *
 * @endpoint POST /form/submissions/{submission_id}/step
 */
async ({ submissionId, formStepUpdate }: { submissionId?: string, formStepUpdate?: FormStepUpdate } = {}): Promise<FormSubmissionRead> => {
        const response = await axios.post(`/form/submissions/${submissionId}/step`, formStepUpdate)
        return ApiResponse.create(response.data, response)},
    processAbandoned: /**
 * Trigger Abandoned Check
 *
 * Manually trigger the abandoned form checker.
 *
 * @endpoint POST /form/submissions/process-abandoned
 */
async ({ _minutesInactive }: { _minutesInactive?: number } = {}): Promise<{ [key: string]: any }> => {
        const response = await axios.post('/form/submissions/process-abandoned', {}, { params: {'minutes_inactive': _minutesInactive} })
        return ApiResponse.create(response.data, response)}
  },
  names: /**
 * Get Form Names
 *
 * Get list of unique form names.
 *
 * @endpoint GET /form/names
 */
async (): Promise<ApiResponse<string[]>> => {
        const response = await axios.get('/form/names')
        return ApiResponse.create(response.data, response)},
  definitions: {
    get: /**
 * Get Form Definition
 *
 * Get a form definition by ID.
 *
 * @endpoint GET /form/definitions/{definition_id}
 */
async ({ definitionId }: { definitionId?: string } = {}): Promise<FormDefinitionRead> => {
        const response = await axios.get(`/form/definitions/${definitionId}`)
        return ApiResponse.create(response.data, response)},
    put: /**
 * Update Form Definition
 *
 * Update a form definition.
 *
 * @endpoint PUT /form/definitions/{definition_id}
 */
async ({ definitionId, formDefinitionUpdate }: { definitionId?: string, formDefinitionUpdate?: FormDefinitionUpdate } = {}): Promise<FormDefinitionRead> => {
        const response = await axios.put(`/form/definitions/${definitionId}`, formDefinitionUpdate)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Form Definition
 *
 * Create a new form definition with JSON Schema validation.
 *
 * @endpoint POST /form/definitions
 */
async ({ formDefinitionCreate }: { formDefinitionCreate?: FormDefinitionCreate } = {}): Promise<FormDefinitionRead> => {
        const response = await axios.post('/form/definitions', formDefinitionCreate)
        return ApiResponse.create(response.data, response)},
    byName: /**
 * Get Form Definition By Name
 *
 * Get a form definition by name.
 *
 * @endpoint GET /form/definitions/by-name/{name}
 */
async ({ name }: { name?: string } = {}): Promise<FormDefinitionRead> => {
        const response = await axios.get(`/form/definitions/by-name/${name}`)
        return ApiResponse.create(response.data, response)},
    resolveNext: /**
 * Resolve Next Step
 *
 * Optional, non-authoritative navigation helper for multi-step forms.
 * 
 * Given the current step key and the accumulated answers, returns the next
 * step key using the same first-match SCIM logic the client uses. A null
 * ``next_key`` (``terminal=true``) means the current step is a leaf and the
 * form is complete. This never gates submission — the client owns navigation.
 *
 * @endpoint POST /form/definitions/{definition_id}/resolve-next
 */
async ({ definitionId, resolveNextRequest }: { definitionId?: string, resolveNextRequest?: ResolveNextRequest } = {}): Promise<ResolveNextResponse> => {
        const response = await axios.post(`/form/definitions/${definitionId}/resolve-next`, resolveNextRequest)
        return ApiResponse.create(response.data, response)}
  },
  validate: /**
 * Validate Form Data
 *
 * Validate form data against a form definition schema.
 *
 * @endpoint POST /form/validate
 */
async ({ validateFormRequest }: { validateFormRequest?: ValidateFormRequest } = {}): Promise<ValidateFormResponse> => {
        const response = await axios.post('/form/validate', validateFormRequest)
        return ApiResponse.create(response.data, response)},
  analytics: {
    count: /**
 * Get Submission Count
 *
 * Get count of form submissions.
 *
 * @endpoint GET /form/analytics/count
 */
async ({ _formName, status }: { _formName?: string, status?: FormSubStatus } = {}): Promise<{ [key: string]: any }> => {
        const response = await axios.get('/form/analytics/count', { params: {'form_name': _formName, status} })
        return ApiResponse.create(response.data, response)},
    conversionRate: /**
 * Get Conversion Rate
 *
 * Get conversion rate for a form.
 *
 * @endpoint GET /form/analytics/conversion-rate/{form_name}
 */
async ({ formName }: { formName?: string } = {}): Promise<{ [key: string]: any }> => {
        const response = await axios.get(`/form/analytics/conversion-rate/${formName}`)
        return ApiResponse.create(response.data, response)},
    utmBreakdown: /**
 * Get Utm Breakdown
 *
 * Get breakdown of submissions by UTM source.
 *
 * @endpoint GET /form/analytics/utm-breakdown
 */
async ({ _formName }: { _formName?: string } = {}): Promise<{ [key: string]: any }> => {
        const response = await axios.get('/form/analytics/utm-breakdown', { params: {'form_name': _formName} })
        return ApiResponse.create(response.data, response)}
  }
};

/**
 * API functions for newsletter endpoints
 */
export const newsletter = {
  send: /**
 * Send Transactional Email
 *
 * Send a transactional email (template_key + context, or subject + body_html).
 *
 * @endpoint POST /newsletter/send
 */
async (): Promise<{ [key: string]: any }> => {
        const response = await axios.post('/newsletter/send', {})
        return ApiResponse.create(response.data, response)},
  templates: {
    getByTemplateId: /**
 * Get Template
 *
 * Get a template by ID.
 *
 * @endpoint GET /newsletter/templates/{template_id}
 */
async ({ templateId }: { templateId?: string } = {}): Promise<EmailTemplateResponse> => {
        const response = await axios.get(`/newsletter/templates/${templateId}`)
        return ApiResponse.create(response.data, response)},
    put: /**
 * Update Template
 *
 * Update an email template.
 *
 * @endpoint PUT /newsletter/templates/{template_id}
 */
async ({ templateId, emailTemplateUpdate }: { templateId?: string, emailTemplateUpdate?: EmailTemplateUpdate } = {}): Promise<EmailTemplateResponse> => {
        const response = await axios.put(`/newsletter/templates/${templateId}`, emailTemplateUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Template
 *
 * Delete a template.
 *
 * @endpoint DELETE /newsletter/templates/{template_id}
 */
async ({ templateId }: { templateId?: string } = {}) => {
        const response = await axios.delete(`/newsletter/templates/${templateId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Template
 *
 * Create a new email template.
 *
 * @endpoint POST /newsletter/templates
 */
async ({ emailTemplateCreate }: { emailTemplateCreate?: EmailTemplateCreate } = {}): Promise<EmailTemplateResponse> => {
        const response = await axios.post('/newsletter/templates', emailTemplateCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Templates
 *
 * List email templates.
 *
 * @endpoint GET /newsletter/templates
 */
async ({ _templateType, _isActive, offset, limit = 100 }: { _templateType?: TemplateType, _isActive?: boolean, offset?: number, limit?: number } = {}): Promise<ApiResponse<EmailTemplateResponse[]>> => {
        const response = await axios.get('/newsletter/templates', { params: {'template_type': _templateType, 'is_active': _isActive, offset, limit} })
        return ApiResponse.create(response.data, response)},
    byKey: /**
 * Get Template By Key
 *
 * Get a template by its unique key.
 *
 * @endpoint GET /newsletter/templates/by-key/{key}
 */
async ({ key }: { key?: string } = {}): Promise<EmailTemplateResponse> => {
        const response = await axios.get(`/newsletter/templates/by-key/${key}`)
        return ApiResponse.create(response.data, response)}
  },
  suppressions: {
    delete: /**
 * Unsuppress Email
 *
 * Remove a suppression (re-allow sending to this address).
 *
 * @endpoint DELETE /newsletter/suppressions/{email}
 */
async ({ email }: { email?: string } = {}) => {
        const response = await axios.delete(`/newsletter/suppressions/${email}`)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Suppressions
 *
 * List globally suppressed email addresses (newest first).
 *
 * @endpoint GET /newsletter/suppressions
 */
async ({ offset, limit = 100 }: { offset?: number, limit?: number } = {}): Promise<ApiResponse<EmailSuppressionResponse[]>> => {
        const response = await axios.get('/newsletter/suppressions', { params: {offset, limit} })
        return ApiResponse.create(response.data, response)},
    post: /**
 * Suppress Email
 *
 * Manually suppress an email address (do-not-email).
 *
 * @endpoint POST /newsletter/suppressions
 */
async ({ suppressEmailRequest }: { suppressEmailRequest?: SuppressEmailRequest } = {}): Promise<EmailSuppressionResponse> => {
        const response = await axios.post('/newsletter/suppressions', suppressEmailRequest)
        return ApiResponse.create(response.data, response)}
  },
  campaigns: {
    getByCampaignId: /**
 * Get Campaign
 *
 * Get a campaign by ID.
 *
 * @endpoint GET /newsletter/campaigns/{campaign_id}
 */
async ({ campaignId }: { campaignId?: string } = {}): Promise<CampaignResponse> => {
        const response = await axios.get(`/newsletter/campaigns/${campaignId}`)
        return ApiResponse.create(response.data, response)},
    put: /**
 * Update Campaign
 *
 * Update a campaign.
 *
 * @endpoint PUT /newsletter/campaigns/{campaign_id}
 */
async ({ campaignId, campaignUpdate }: { campaignId?: string, campaignUpdate?: CampaignUpdate } = {}): Promise<CampaignResponse> => {
        const response = await axios.put(`/newsletter/campaigns/${campaignId}`, campaignUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Campaign
 *
 * Delete a campaign.
 *
 * @endpoint DELETE /newsletter/campaigns/{campaign_id}
 */
async ({ campaignId }: { campaignId?: string } = {}) => {
        const response = await axios.delete(`/newsletter/campaigns/${campaignId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Campaign
 *
 * Create a new campaign targeting a segment.
 *
 * @endpoint POST /newsletter/campaigns
 */
async ({ campaignCreate }: { campaignCreate?: CampaignCreate } = {}): Promise<CampaignResponse> => {
        const response = await axios.post('/newsletter/campaigns', campaignCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Campaigns
 *
 * List campaigns.
 *
 * @endpoint GET /newsletter/campaigns
 */
async ({ _campaignStatus, offset, limit = 100 }: { _campaignStatus?: CampaignStatus, offset?: number, limit?: number } = {}): Promise<ApiResponse<CampaignResponse[]>> => {
        const response = await axios.get('/newsletter/campaigns', { params: {'campaign_status': _campaignStatus, offset, limit} })
        return ApiResponse.create(response.data, response)},
    stats: /**
 * Get Campaign Stats
 *
 * Get campaign statistics.
 *
 * @endpoint GET /newsletter/campaigns/{campaign_id}/stats
 */
async ({ campaignId }: { campaignId?: string } = {}): Promise<CampaignStatsResponse> => {
        const response = await axios.get(`/newsletter/campaigns/${campaignId}/stats`)
        return ApiResponse.create(response.data, response)},
    schedule: /**
 * Schedule Campaign
 *
 * Schedule a campaign for sending.
 *
 * @endpoint POST /newsletter/campaigns/{campaign_id}/schedule
 */
async ({ campaignId }: { campaignId?: string } = {}): Promise<CampaignResponse> => {
        const response = await axios.post(`/newsletter/campaigns/${campaignId}/schedule`, {})
        return ApiResponse.create(response.data, response)},
    send: /**
 * Send Campaign
 *
 * Send a campaign to its segment audience (suppression-filtered).
 *
 * @endpoint POST /newsletter/campaigns/{campaign_id}/send
 */
async ({ campaignId }: { campaignId?: string } = {}): Promise<{ [key: string]: any }> => {
        const response = await axios.post(`/newsletter/campaigns/${campaignId}/send`, {})
        return ApiResponse.create(response.data, response)},
    test: /**
 * Send Test Email
 *
 * Send a one-off test email to a single address (action only, no record).
 *
 * @endpoint POST /newsletter/campaigns/test
 */
async ({ sendTestEmailRequest }: { sendTestEmailRequest?: SendTestEmailRequest } = {}): Promise<{ [key: string]: any }> => {
        const response = await axios.post('/newsletter/campaigns/test', sendTestEmailRequest)
        return ApiResponse.create(response.data, response)}
  },
  p: /**
 * Track Email Open
 *
 * Track email open via transparent 1x1 pixel.
 *
 * @endpoint GET /newsletter/p/{token}
 */
async ({ token }: { token?: string } = {}) => {
        const response = await axios.get(`/newsletter/p/${token}`)
        return ApiResponse.create(response.data, response)},
  r: /**
 * Track Link Click
 *
 * Track link click and redirect to the original URL.
 *
 * @endpoint GET /newsletter/r/{token}
 */
async ({ token, url }: { token?: string, url?: string } = {}) => {
        const response = await axios.get(`/newsletter/r/${token}`, { params: {url} })
        return ApiResponse.create(response.data, response)},
  unsubscribe: {
    post: /**
 * Unsubscribe
 *
 * Unsubscribe (API): suppress the email carried by a signed token.
 *
 * @endpoint POST /newsletter/unsubscribe
 */
async ({ unsubscribeRequest }: { unsubscribeRequest?: UnsubscribeRequest } = {}): Promise<EmailSuppressionResponse> => {
        const response = await axios.post('/newsletter/unsubscribe', unsubscribeRequest)
        return ApiResponse.create(response.data, response)},
    get: /**
 * Unsubscribe Page
 *
 * Show the unsubscribe confirmation page.
 *
 * @endpoint GET /newsletter/unsubscribe
 */
async ({ token }: { token?: string } = {}) => {
        const response = await axios.get('/newsletter/unsubscribe', { params: {token} })
        return ApiResponse.create(response.data, response)},
    confirm: /**
 * Unsubscribe Confirm
 *
 * Handle the unsubscribe form submission (token-based, global suppression).
 *
 * @endpoint POST /newsletter/unsubscribe/confirm
 */
async () => {
        const response = await axios.post('/newsletter/unsubscribe/confirm', {})
        return ApiResponse.create(response.data, response)}
  }
};

/**
 * API functions for files endpoints
 */
export const files = {
  getByPathKey: /**
 * Get File
 *
 * Get file by path key.
 * 
 * Example:
 *     GET /files/customer-123/profile.jpg
 *
 * @endpoint GET /files/{path_key}
 */
async ({ pathKey }: { pathKey?: string } = {}): Promise<FileResponse> => {
        const response = await axios.get(`/files/${pathKey}`)
        return ApiResponse.create(response.data, response)},
  delete: /**
 * Delete File
 *
 * Delete file.
 * 
 * Example:
 *     DELETE /files/customer-123/profile.jpg
 *
 * @endpoint DELETE /files/{path_key}
 */
async ({ pathKey }: { pathKey?: string } = {}) => {
        const response = await axios.delete(`/files/${pathKey}`)
        return ApiResponse.create(response.data, response)},
  patch: /**
 * Update File
 *
 * Update file - tags and/or meta.
 * 
 * Examples:
 *     # Add tags
 *     PATCH /files/customer-123/file.pdf?add_tags=verified&add_tags=important
 * 
 *     # Set tags
 *     PATCH /files/customer-123/file.pdf?set_tags=contract&set_tags=final
 * 
 *     # Update meta
 *     PATCH /files/customer-123/file.pdf
 *     Body: {author: "John Doe", department: "Sales"}
 * 
 *     # Update both
 *     PATCH /files/customer-123/file.pdf?add_tags=verified
 *     Body: {author: "John Doe"}
 * 
 * Query Parameters:
 *     - add_tags: Tags to add (can specify multiple)
 *     - remove_tags: Tags to remove (can specify multiple)
 *     - set_tags: Set to these tags (replaces all)
 * 
 * Body:
 *     - meta: Dict to merge with existing meta
 *
 * @endpoint PATCH /files/{path_key}
 */
async ({ pathKey, _addTags, _removeTags, _setTags, meta }: { pathKey?: string, _addTags?: string[], _removeTags?: string[], _setTags?: string[], meta?: { [key: string]: any } } = {}): Promise<FileResponse> => {
        const response = await axios.patch(`/files/${pathKey}`, meta, { params: {'add_tags': _addTags, 'remove_tags': _removeTags, 'set_tags': _setTags} })
        return ApiResponse.create(response.data, response)},
  get: /**
 * Query Files Endpoint
 *
 * Query files with filters - POWERFUL!
 * 
 * Examples:
 *     # All files for customer
 *     GET /files?ref_id=customer-123
 * 
 *     # Customer's contracts
 *     GET /files?ref_id=customer-123&has_tag=contract
 * 
 *     # Signed contracts from 2024
 *     GET /files?has_all_tags=contract&has_all_tags=signed&has_all_tags=2024
 * 
 *     # Files in directory
 *     GET /files?ref_id=customer-123&dir_path=deals/deal-456
 * 
 * Query Parameters:
 *     - ref_id: Filter by entity reference
 *     - dir_path: Filter by directory (prefix match)
 *     - has_tag: Has this tag
 *     - has_any_tags: Has any of these tags (can specify multiple)
 *     - has_all_tags: Has all of these tags (can specify multiple)
 *     - offset: Pagination offset
 *     - limit: Max results (1-1000)
 *
 * @endpoint GET /files
 */
async ({ _refId, _dirPath, _hasTag, _hasAnyTags, _hasAllTags, offset, limit = 100 }: { _refId?: string, _dirPath?: string, _hasTag?: string, _hasAnyTags?: string[], _hasAllTags?: string[], offset?: number, limit?: number } = {}): Promise<ApiResponse<FileResponse[]>> => {
        const response = await axios.get('/files', { params: {'ref_id': _refId, 'dir_path': _dirPath, 'has_tag': _hasTag, 'has_any_tags': _hasAnyTags, 'has_all_tags': _hasAllTags, offset, limit} })
        return ApiResponse.create(response.data, response)},
  upload: /**
 * Upload File
 *
 * Upload a file - SIMPLE!
 * 
 * Examples:
 *     POST /files/upload
 *         ?ref_id=customer-123
 *         &dir_path=deals/deal-456
 *         &tags=contract
 *         &tags=signed
 * 
 * Query Parameters:
 *     - ref_id: Link to entity (customer ID, deal ID, etc.)
 *     - dir_path: Directory within ref_id
 *     - tags: Tags (can specify multiple)
 *     - metadata: JSON metadata
 *     - override_existing: Override if file exists
 *
 * @endpoint POST /files/upload
 */
async ({ formData }: { formData?: FormData } = {}): Promise<FileResponse> => {
        const response = await axios.post('/files/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
        return ApiResponse.create(response.data, response)},
  attachments: {
    delete: /**
 * Remove Attachment
 *
 * Detach a file from a record (does NOT delete the file itself).
 *
 * @endpoint DELETE /files/attachments/{attachment_id}
 */
async ({ attachmentId }: { attachmentId?: string } = {}) => {
        const response = await axios.delete(`/files/attachments/${attachmentId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Attachment
 *
 * Attach a file to a domain record.
 *
 * @endpoint POST /files/attachments
 */
async ({ attachRequest }: { attachRequest?: AttachRequest } = {}): Promise<FileAttachmentRead> => {
        const response = await axios.post('/files/attachments', attachRequest)
        return ApiResponse.create(response.data, response)},
    get: /**
 * Query Attachments
 *
 * List file attachments for a record.
 *
 * @endpoint GET /files/attachments
 */
async ({ _recordId, _recordType, slot }: { _recordId?: string, _recordType?: string, slot?: string } = {}): Promise<ApiResponse<FileAttachmentRead[]>> => {
        const response = await axios.get('/files/attachments', { params: {'record_id': _recordId, 'record_type': _recordType, slot} })
        return ApiResponse.create(response.data, response)},
    batch: /**
 * Create Attachments Batch
 *
 * Attach multiple files to a domain record.
 *
 * @endpoint POST /files/attachments/batch
 */
async ({ attachManyRequest }: { attachManyRequest?: AttachManyRequest } = {}): Promise<ApiResponse<FileAttachmentRead[]>> => {
        const response = await axios.post('/files/attachments/batch', attachManyRequest)
        return ApiResponse.create(response.data, response)}
  }
};

/**
 * API functions for automations endpoints
 */
export const automations = {
  getByAutomationId: /**
 * Get Automation
 *
 * Get a specific automation.
 *
 * @endpoint GET /automations/{automation_id}
 */
async ({ automationId }: { automationId?: string } = {}): Promise<AutomationResponse> => {
        const response = await axios.get(`/automations/${automationId}`)
        return ApiResponse.create(response.data, response)},
  put: /**
 * Update Automation
 *
 * Update an automation.
 *
 * @endpoint PUT /automations/{automation_id}
 */
async ({ automationId, automationUpdate }: { automationId?: string, automationUpdate?: AutomationUpdate } = {}): Promise<AutomationResponse> => {
        const response = await axios.put(`/automations/${automationId}`, automationUpdate)
        return ApiResponse.create(response.data, response)},
  delete: /**
 * Delete Automation
 *
 * Delete an automation.
 *
 * @endpoint DELETE /automations/{automation_id}
 */
async ({ automationId }: { automationId?: string } = {}) => {
        const response = await axios.delete(`/automations/${automationId}`)
        return ApiResponse.create(response.data, response)},
  get: /**
 * List Automations
 *
 * List all automations with optional filtering.
 *
 * @endpoint GET /automations
 */
async ({ _enabledOnly, _triggerEvent, _createdBy, search, offset, limit = 50 }: { _enabledOnly?: boolean, _triggerEvent?: string, _createdBy?: string, search?: string, offset?: number, limit?: number } = {}): Promise<ApiResponse<AutomationResponse[]>> => {
        const response = await axios.get('/automations', { params: {'enabled_only': _enabledOnly, 'trigger_event': _triggerEvent, 'created_by': _createdBy, search, offset, limit} })
        return ApiResponse.create(response.data, response)},
  post: /**
 * Create Automation
 *
 * Create a new automation.
 *
 * @endpoint POST /automations
 */
async ({ automationCreate }: { automationCreate?: AutomationCreate } = {}): Promise<AutomationResponse> => {
        const response = await axios.post('/automations', automationCreate)
        return ApiResponse.create(response.data, response)},
  discover: /**
 * Discover Actions
 *
 * Discover all available actions for automation workflows.
 * 
 * Returns service methods that can be called in automation steps.
 * Each action includes:
 * - name: "service.method" format (e.g., "whatsapp.send_template_message")
 * - description: What the action does
 * - inputSchema: JSON Schema for parameters
 *
 * @endpoint GET /automations/discover
 */
async (): Promise<ApiResponse<ToolDefinition[]>> => {
        const response = await axios.get('/automations/discover')
        return ApiResponse.create(response.data, response)},
  ai: /**
 * Ai Build Automation
 *
 * Build an automation from a natural-language description.
 * 
 * The AI either creates the automation directly or returns clarifying
 * questions.  For multi-turn flows, pass prior turns in ``history``.
 *
 * @endpoint POST /automations/ai
 */
async ({ buildAutomationRequest }: { buildAutomationRequest?: BuildAutomationRequest } = {}): Promise<BuilderResponse> => {
        const response = await axios.post('/automations/ai', buildAutomationRequest)
        return ApiResponse.create(response.data, response)},
  toggle: /**
 * Toggle Automation
 *
 * Toggle automation enabled/disabled.
 *
 * @endpoint PATCH /automations/{automation_id}/toggle
 */
async ({ automationId }: { automationId?: string } = {}): Promise<AutomationResponse> => {
        const response = await axios.patch(`/automations/${automationId}/toggle`, {})
        return ApiResponse.create(response.data, response)},
  executions: {
    getByExecutionId: /**
 * Get Execution
 *
 * Get a specific execution.
 *
 * @endpoint GET /automations/executions/{execution_id}
 */
async ({ executionId }: { executionId?: string } = {}): Promise<AutomationExecutionResponse> => {
        const response = await axios.get(`/automations/executions/${executionId}`)
        return ApiResponse.create(response.data, response)},
    get: /**
 * Get Automation Executions
 *
 * Get execution history for an automation.
 *
 * @endpoint GET /automations/{automation_id}/executions
 */
async ({ automationId, offset, limit = 50 }: { automationId?: string, offset?: number, limit?: number } = {}): Promise<ApiResponse<AutomationExecutionResponse[]>> => {
        const response = await axios.get(`/automations/${automationId}/executions`, { params: {offset, limit} })
        return ApiResponse.create(response.data, response)}
  },
  test: /**
 * Test Automation
 *
 * Test an automation with sample data.
 *
 * @endpoint POST /automations/{automation_id}/test
 */
async ({ automationId, testAutomationRequest }: { automationId?: string, testAutomationRequest?: TestAutomationRequest } = {}): Promise<TestAutomationResult> => {
        const response = await axios.post(`/automations/${automationId}/test`, testAutomationRequest)
        return ApiResponse.create(response.data, response)},
  operators: {
    all: /**
 * Get Operators
 *
 * Get all available condition operators.
 *
 * @endpoint GET /automations/operators/all
 */
async (): Promise<ApiResponse<OperatorInfo[]>> => {
        const response = await axios.get('/automations/operators/all')
        return ApiResponse.create(response.data, response)}
  },
  triggers: {
    all: /**
 * Get Triggers
 *
 * Get all triggerable events with their supported trigger_params.
 * 
 * Returns only events that have declared trigger_params.
 *
 * @endpoint GET /automations/triggers/all
 */
async (): Promise<ApiResponse<{ [key: string]: any }[]>> => {
        const response = await axios.get('/automations/triggers/all')
        return ApiResponse.create(response.data, response)}
  },
  stats: {
    overview: /**
 * Get Automation Stats
 *
 * Get automation statistics.
 *
 * @endpoint GET /automations/stats/overview
 */
async (): Promise<AutomationStats> => {
        const response = await axios.get('/automations/stats/overview')
        return ApiResponse.create(response.data, response)}
  }
};

/**
 * API functions for agents endpoints
 */
export const agents = {
  getByAgentId: /**
 * Get Agent
 *
 * Get an agent by ID.
 *
 * @endpoint GET /agents/{agent_id}
 */
async ({ agentId }: { agentId?: string } = {}): Promise<AgentResponse> => {
        const response = await axios.get(`/agents/${agentId}`)
        return ApiResponse.create(response.data, response)},
  patch: /**
 * Update Agent
 *
 * Update an agent.
 *
 * @endpoint PATCH /agents/{agent_id}
 */
async ({ agentId, agentUpdate }: { agentId?: string, agentUpdate?: AgentUpdate } = {}): Promise<AgentResponse> => {
        const response = await axios.patch(`/agents/${agentId}`, agentUpdate)
        return ApiResponse.create(response.data, response)},
  delete: /**
 * Delete Agent
 *
 * Delete an agent.
 *
 * @endpoint DELETE /agents/{agent_id}
 */
async ({ agentId }: { agentId?: string } = {}) => {
        const response = await axios.delete(`/agents/${agentId}`)
        return ApiResponse.create(response.data, response)},
  post: /**
 * Create Agent
 *
 * Create a new agent.
 *
 * @endpoint POST /agents/
 */
async ({ agentCreate }: { agentCreate?: AgentCreate } = {}): Promise<AgentResponse> => {
        const response = await axios.post('/agents/', agentCreate)
        return ApiResponse.create(response.data, response)},
  get: /**
 * List Agents
 *
 * List agents with optional filtering.
 *
 * @endpoint GET /agents/
 */
async ({ status, skip, limit = 100 }: { status?: AgentStatus, skip?: number, limit?: number } = {}): Promise<ApiResponse<AgentResponse[]>> => {
        const response = await axios.get('/agents/', { params: {status, skip, limit} })
        return ApiResponse.create(response.data, response)},
  tools: {
    get: /**
 * List Tools
 *
 * List all available tools in the registry.
 *
 * @endpoint GET /agents/tools
 */
async (): Promise<ToolsListResponse> => {
        const response = await axios.get('/agents/tools')
        return ApiResponse.create(response.data, response)},
    add: /**
 * Add Agent Tools
 *
 * Add tools to an agent (avoiding duplicates).
 *
 * @endpoint POST /agents/{agent_id}/tools/add
 */
async ({ agentId, agentToolsUpdate }: { agentId?: string, agentToolsUpdate?: AgentToolsUpdate } = {}): Promise<AgentResponse> => {
        const response = await axios.post(`/agents/${agentId}/tools/add`, agentToolsUpdate)
        return ApiResponse.create(response.data, response)},
    remove: /**
 * Remove Agent Tools
 *
 * Remove tools from an agent.
 *
 * @endpoint POST /agents/{agent_id}/tools/remove
 */
async ({ agentId, agentToolsUpdate }: { agentId?: string, agentToolsUpdate?: AgentToolsUpdate } = {}): Promise<AgentResponse> => {
        const response = await axios.post(`/agents/${agentId}/tools/remove`, agentToolsUpdate)
        return ApiResponse.create(response.data, response)}
  },
  models: /**
 * List Models
 *
 * List all available models from all registered providers.
 *
 * @endpoint GET /agents/models
 */
async (): Promise<ApiResponse<ModelInfo[]>> => {
        const response = await axios.get('/agents/models')
        return ApiResponse.create(response.data, response)},
  skills: {
    getBySkillId: /**
 * Get Skill
 *
 * Get a skill by ID.
 *
 * @endpoint GET /agents/skills/{skill_id}
 */
async ({ skillId }: { skillId?: string } = {}): Promise<SkillResponse> => {
        const response = await axios.get(`/agents/skills/${skillId}`)
        return ApiResponse.create(response.data, response)},
    patch: /**
 * Update Skill
 *
 * Update a skill.
 *
 * @endpoint PATCH /agents/skills/{skill_id}
 */
async ({ skillId, skillUpdate }: { skillId?: string, skillUpdate?: SkillUpdate } = {}): Promise<SkillResponse> => {
        const response = await axios.patch(`/agents/skills/${skillId}`, skillUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Skill
 *
 * Delete a skill.
 *
 * @endpoint DELETE /agents/skills/{skill_id}
 */
async ({ skillId }: { skillId?: string } = {}) => {
        const response = await axios.delete(`/agents/skills/${skillId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Skill
 *
 * Create a new skill.
 *
 * @endpoint POST /agents/skills
 */
async ({ skillCreate }: { skillCreate?: SkillCreate } = {}): Promise<SkillResponse> => {
        const response = await axios.post('/agents/skills', skillCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Skills
 *
 * List skills.
 *
 * @endpoint GET /agents/skills
 */
async ({ _isActive, skip, limit = 100 }: { _isActive?: boolean, skip?: number, limit?: number } = {}): Promise<ApiResponse<SkillResponse[]>> => {
        const response = await axios.get('/agents/skills', { params: {'is_active': _isActive, skip, limit} })
        return ApiResponse.create(response.data, response)},
    add: /**
 * Add Agent Skills
 *
 * Add skills to an agent (avoiding duplicates).
 *
 * @endpoint POST /agents/{agent_id}/skills/add
 */
async ({ agentId, agentSkillsUpdate }: { agentId?: string, agentSkillsUpdate?: AgentSkillsUpdate } = {}): Promise<AgentResponse> => {
        const response = await axios.post(`/agents/${agentId}/skills/add`, agentSkillsUpdate)
        return ApiResponse.create(response.data, response)},
    remove: /**
 * Remove Agent Skills
 *
 * Remove skills from an agent.
 *
 * @endpoint POST /agents/{agent_id}/skills/remove
 */
async ({ agentId, agentSkillsUpdate }: { agentId?: string, agentSkillsUpdate?: AgentSkillsUpdate } = {}): Promise<AgentResponse> => {
        const response = await axios.post(`/agents/${agentId}/skills/remove`, agentSkillsUpdate)
        return ApiResponse.create(response.data, response)}
  },
  conversations: {
    patch: /**
 * Update Conversation
 *
 * Patch conversation settings (title, HITL auto-approve toggle).
 *
 * @endpoint PATCH /agents/conversations/{conversation_id}
 */
async ({ conversationId, conversationUpdate }: { conversationId?: string, conversationUpdate?: ConversationUpdate } = {}): Promise<AgentConversationResponse> => {
        const response = await axios.patch(`/agents/conversations/${conversationId}`, conversationUpdate)
        return ApiResponse.create(response.data, response)},
    getByConversationId: /**
 * Get Conversation
 *
 * Get a conversation.
 *
 * @endpoint GET /agents/conversations/{conversation_id}
 */
async ({ conversationId }: { conversationId?: string } = {}): Promise<AgentConversationResponse> => {
        const response = await axios.get(`/agents/conversations/${conversationId}`)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Conversation
 *
 * Delete a conversation and all its messages.
 *
 * @endpoint DELETE /agents/conversations/{conversation_id}
 */
async ({ conversationId }: { conversationId?: string } = {}) => {
        const response = await axios.delete(`/agents/conversations/${conversationId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Conversation
 *
 * Create a new conversation.
 *
 * @endpoint POST /agents/conversations
 */
async ({ conversationCreate }: { conversationCreate?: ConversationCreate } = {}): Promise<AgentConversationResponse> => {
        const response = await axios.post('/agents/conversations', conversationCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Conversations
 *
 * List conversations with optional filtering.
 * 
 * Non-admin users can only see their own conversations.
 * 
 * Query parameters:
 * - user_id: Filter by user ID (admins only; ignored for regular users)
 * - agent_id: Filter by agent ID
 * - ref_id: Filter by loose reference (e.g. case_id, problem_id)
 * - skip: Number of records to skip (default: 0)
 * - limit: Maximum records to return (default: 100)
 *
 * @endpoint GET /agents/conversations
 */
async ({ _userId, _agentId, _refId, skip, limit = 100 }: { _userId?: string, _agentId?: string, _refId?: string, skip?: number, limit?: number } = {}): Promise<ApiResponse<AgentConversationResponse[]>> => {
        const response = await axios.get('/agents/conversations', { params: {'user_id': _userId, 'agent_id': _agentId, 'ref_id': _refId, skip, limit} })
        return ApiResponse.create(response.data, response)},
    artifacts: /**
 * List Artifacts
 *
 * List artifacts belonging to a conversation.
 *
 * @endpoint GET /agents/conversations/{conversation_id}/artifacts
 */
async ({ conversationId, skip, limit = 50 }: { conversationId?: string, skip?: number, limit?: number } = {}): Promise<ApiResponse<ArtifactResponse[]>> => {
        const response = await axios.get(`/agents/conversations/${conversationId}/artifacts`, { params: {skip, limit} })
        return ApiResponse.create(response.data, response)},
    search: /**
 * Search Conversations
 *
 * Search conversations by title or message content.
 * 
 * Non-admin users can only search their own conversations.
 * 
 * Query parameters:
 * - q: Search query string (required, case-insensitive)
 * - ref_id: Filter by ref_id to reduce load (optional)
 * - user_id: Filter by user_id to reduce load (optional, admins only)
 * 
 * Searches both conversation titles and message content.
 *
 * @endpoint GET /agents/conversations/search
 */
async ({ q, _refId, _userId, skip, limit = 100 }: { q?: string, _refId?: string, _userId?: string, skip?: number, limit?: number } = {}): Promise<ApiResponse<AgentConversationResponse[]>> => {
        const response = await axios.get('/agents/conversations/search', { params: {q, 'ref_id': _refId, 'user_id': _userId, skip, limit} })
        return ApiResponse.create(response.data, response)},
    messages: {
      delete: /**
 * Delete Message
 *
 * Delete a single message from a conversation.
 *
 * @endpoint DELETE /agents/conversations/{conversation_id}/messages/{message_id}
 */
async ({ conversationId, messageId }: { conversationId?: string, messageId?: string } = {}) => {
        const response = await axios.delete(`/agents/conversations/${conversationId}/messages/${messageId}`)
        return ApiResponse.create(response.data, response)},
      patch: /**
 * Update Message
 *
 * Update fields on an existing message.
 *
 * @endpoint PATCH /agents/conversations/{conversation_id}/messages/{message_id}
 */
async ({ conversationId, messageId, updateMessageRequest }: { conversationId?: string, messageId?: string, updateMessageRequest?: UpdateMessageRequest } = {}): Promise<AgentMessageResponse> => {
        const response = await axios.patch(`/agents/conversations/${conversationId}/messages/${messageId}`, updateMessageRequest)
        return ApiResponse.create(response.data, response)},
      get: /**
 * Get Conversation History
 *
 * Get conversation message history with pagination.
 * 
 * Default limit is 500 (was 100): long-running case conversations routinely
 * exceed 100 messages, and the service now returns the most RECENT ``limit``
 * messages, so a low limit hid a client's latest turns (looked like deleted
 * messages). 500 comfortably covers real conversations while the newest-window
 * logic bounds the worst case.
 *
 * @endpoint GET /agents/conversations/{conversation_id}/messages
 */
async ({ conversationId, limit = 500 }: { conversationId?: string, limit?: number } = {}): Promise<ConversationHistoryResponse> => {
        const response = await axios.get(`/agents/conversations/${conversationId}/messages`, { params: {limit} })
        return ApiResponse.create(response.data, response)},
      post: /**
 * Start an agent run (returns run_id for SSE stream)
 *
 * Send a message to the agent.  Returns immediately with a ``run_id``.
 * 
 * Open ``GET /conversations/{id}/messages/stream?run_id={run_id}`` to receive
 * the token stream as Server-Sent Events.  Both calls can be fired in parallel —
 * events are buffered server-side so no tokens are lost.
 *
 * @endpoint POST /agents/conversations/{conversation_id}/messages
 */
async ({ conversationId, _xLangsmithParentRunId, _xLangsmithSessionId, sendAgentMessageRequest }: { conversationId?: string, _xLangsmithParentRunId?: string, _xLangsmithSessionId?: string, sendAgentMessageRequest?: SendAgentMessageRequest } = {}): Promise<StartMessageRunResponse> => {
        const response = await axios.post(`/agents/conversations/${conversationId}/messages`, sendAgentMessageRequest, { params: {'X-Langsmith-Parent-Run-Id': _xLangsmithParentRunId, 'X-Langsmith-Session-Id': _xLangsmithSessionId} })
        return ApiResponse.create(response.data, response)},
      sync: /**
 * Send a message and wait for the full response (blocking)
 *
 * Send a message to the agent using standard LangChain format.
 * 
 * **Deprecated** — prefer ``POST /messages`` (returns ``run_id``) +
 * ``GET /messages/stream`` for incremental streaming. This blocking endpoint
 * waits for the full agent response before returning.
 * 
 * Accepts content as:
 * - String: "Hello, how are you?"
 * - Typed list: [MessageContentText, MessageContentImageUrl, ...]
 * 
 * See: https://python.langchain.com/docs/concepts/messages
 *
 * @endpoint POST /agents/conversations/{conversation_id}/messages/sync
 */
async ({ conversationId, sendAgentMessageRequest }: { conversationId?: string, sendAgentMessageRequest?: SendAgentMessageRequest } = {}): Promise<AgentMessageResponse> => {
        const response = await axios.post(`/agents/conversations/${conversationId}/messages/sync`, sendAgentMessageRequest)
        return ApiResponse.create(response.data, response)},
      inject: /**
 * Inject Message
 *
 * Inject a message into a conversation without triggering an AI response.
 * 
 * Use for representative/human messages that should appear in the conversation
 * history but must not invoke the agent (e.g. a rep joining the conversation).
 *
 * @endpoint POST /agents/conversations/{conversation_id}/messages/inject
 */
async ({ conversationId, injectMessageRequest }: { conversationId?: string, injectMessageRequest?: InjectMessageRequest } = {}): Promise<AgentMessageResponse> => {
        const response = await axios.post(`/agents/conversations/${conversationId}/messages/inject`, injectMessageRequest)
        return ApiResponse.create(response.data, response)},
      stream: {
        get: /**
 * Subscribe to agent run stream (SSE)
 *
 * Server-Sent Events stream for an agent run. Pass the run_id returned by POST /messages.
 * 
 * 1. **user_message_saved**: user_message_saved event
 * 2. **skill_activated**: skill_activated event
 * 3. **middleware**: middleware event
 * 4. **reasoning**: reasoning event
 * 5. **token**: token event
 * 6. **tool_call**: tool_call event
 * 7. **tool_result**: tool_result event
 * 8. **done**: done event
 * 9. **error**: error event
 *
 * @endpoint GET /agents/conversations/{conversation_id}/messages/stream
 */
{
  /**
   * Stream SSE events from this endpoint (returns StreamController)
   * Event types: "done", "error", "middleware", "reasoning", "skill_activated", "token", "tool_call", "tool_result", "user_message_saved"
   * 
   * @example
   * const stream = api.endpoint.stream(params)
   *   .on('done', (data) => console.log(data))
   *   .on('error', (data) => console.log(data))
   *   .on('middleware', (data) => console.log(data))
   *   .on('reasoning', (data) => console.log(data))
   *   .on('skill_activated', (data) => console.log(data))
   *   .on('token', (data) => console.log(data))
   *   .on('tool_call', (data) => console.log(data))
   *   .on('tool_result', (data) => console.log(data))
   *   .on('user_message_saved', (data) => console.log(data))
   *   .on('error', (err) => console.error(err))
   *   .on('complete', () => console.log('Done!'))
   * 
   * // Close stream when needed
   * stream.close()
   */
  stream: ({ conversationId, _runId, _lastEventID }: { conversationId?: string, _runId?: string, _lastEventID?: string } = {}, options?: SSEStreamOptions): StreamController<AgentsConversationsMessagesStreamStreamEvents> => {
    const __qs = new URLSearchParams()
    if (_runId) __qs.set('run_id', String(_runId))
    if (_lastEventID) __qs.set('Last-Event-ID', String(_lastEventID))
    const __qsSuffix = __qs.size ? "?" + __qs.toString() : ""
    const url = `${getStreamBaseURL()}/agents/conversations/${conversationId}/messages/stream` + __qsSuffix
    return createSSEStream<AgentsConversationsMessagesStreamStreamEvents>(url, options)
  },
  /**
   * Regular GET request (non-streaming)
   */
  get: async ({ conversationId, _runId, _lastEventID }: { conversationId?: string, _runId?: string, _lastEventID?: string } = {}): Promise<AxiosResponse<any>> => {
    return axios.get(`/agents/conversations/${conversationId}/messages/stream`, { params: {'run_id': _runId, 'Last-Event-ID': _lastEventID} })
  }
},
        post: /**
 * Send Message Stream
 *
 * Send a message and stream the response as Server-Sent Events.
 * 
 * ## Event Types
 * 
 * 1. **user_message_saved** — user message saved to DB (`message_id`)
 * 2. **skill_activated** — skill loaded (`skill_name`, `description`)
 * 3. **middleware** — middleware status (`middleware_type`, `action`, `details`)
 * 4. **reasoning** — thinking delta from reasoning models (`delta`, `message_id`)
 * 5. **token** — content delta (`delta`, `message_id`, `role`)
 * 6. **tool_call** — tool invoked (`call_id`, `tool_name`, `arguments`, `requires_approval`)
 * 7. **tool_result** — tool result (`call_id`, `tool_name`, `result`, `error`)
 * 8. **done** — stream complete (`reason`, `message_id`, `usage`)
 * 9. **error** — error occurred (`error.code`, `error.message`, `error.retryable`)
 * 
 * ## SSE Format
 * ```
 * event: <event_type>
 * data: <json_payload>
 * 
 * ```
 *
 * @endpoint POST /agents/conversations/{conversation_id}/messages/stream
 */
{
  /**
   * Stream SSE events from this endpoint (returns StreamController)
   * Event types: "done", "error", "middleware", "reasoning", "skill_activated", "token", "tool_call", "tool_result", "user_message_saved"
   * 
   * @example
   * const stream = api.endpoint.stream(params)
   *   .on('done', (data) => console.log(data))
   *   .on('error', (data) => console.log(data))
   *   .on('middleware', (data) => console.log(data))
   *   .on('reasoning', (data) => console.log(data))
   *   .on('skill_activated', (data) => console.log(data))
   *   .on('token', (data) => console.log(data))
   *   .on('tool_call', (data) => console.log(data))
   *   .on('tool_result', (data) => console.log(data))
   *   .on('user_message_saved', (data) => console.log(data))
   *   .on('error', (err) => console.error(err))
   *   .on('complete', () => console.log('Done!'))
   * 
   * // Close stream when needed
   * stream.close()
   */
  stream: ({ conversationId, _lastEventID, _xLangsmithParentRunId, _xLangsmithSessionId, sendAgentMessageRequest }: { conversationId?: string, _lastEventID?: string, _xLangsmithParentRunId?: string, _xLangsmithSessionId?: string, sendAgentMessageRequest?: SendAgentMessageRequest } = {}, options?: SSEStreamOptions): StreamController<AgentsConversationsMessagesStreamStreamEvents> => {
    const url = `${getStreamBaseURL()}/agents/conversations/${conversationId}/messages/stream`
    return createSSEStreamPost<AgentsConversationsMessagesStreamStreamEvents>(url, sendAgentMessageRequest, options)
  },
  /**
   * Regular POST request (non-streaming)
   */
  post: async ({ conversationId, _lastEventID, _xLangsmithParentRunId, _xLangsmithSessionId, sendAgentMessageRequest }: { conversationId?: string, _lastEventID?: string, _xLangsmithParentRunId?: string, _xLangsmithSessionId?: string, sendAgentMessageRequest?: SendAgentMessageRequest } = {}): Promise<AxiosResponse<any>> => {
    return axios.post(`/agents/conversations/${conversationId}/messages/stream`, sendAgentMessageRequest)
  }
}
      }
    },
    compress: /**
 * Compress Conversation Context
 *
 * Summarize old messages to free up context window space.
 * 
 * Compresses all but the ``keep_last_n`` most-recent messages into a rolling
 * summary stored on the conversation. Future messages will include the summary
 * as leading context instead of the raw history, allowing the conversation to
 * continue indefinitely without losing important context.
 *
 * @endpoint POST /agents/conversations/{conversation_id}/compress
 */
async ({ conversationId, request }: { conversationId?: string, request?: CompressContextRequest } = {}): Promise<CompressContextResponse> => {
        const response = await axios.post(`/agents/conversations/${conversationId}/compress`, request)
        return ApiResponse.create(response.data, response)},
    interrupts: /**
 * List Pending Interrupts
 *
 * List tool calls paused for human approval in this conversation.
 *
 * @endpoint GET /agents/conversations/{conversation_id}/interrupts
 */
async ({ conversationId }: { conversationId?: string } = {}): Promise<ApiResponse<PendingInterrupt[]>> => {
        const response = await axios.get(`/agents/conversations/${conversationId}/interrupts`)
        return ApiResponse.create(response.data, response)},
    resume: /**
 * Resume Interrupted Run
 *
 * Resume a paused run with a human decision (approve / edit / reject).
 * 
 * On ``approve`` the paused tool executes; on ``edit`` it executes with
 * ``edited_args``; on ``reject`` it is skipped with an optional ``reason``.
 * The run then continues to completion.
 *
 * @endpoint POST /agents/conversations/{conversation_id}/resume
 */
async ({ conversationId, resumeRunRequest }: { conversationId?: string, resumeRunRequest?: ResumeRunRequest } = {}): Promise<AgentMessageResponse> => {
        const response = await axios.post(`/agents/conversations/${conversationId}/resume`, resumeRunRequest)
        return ApiResponse.create(response.data, response)},
    queue: {
      put: /**
 * Update a queued message
 *
 * Replace the content of a queued message.
 *
 * @endpoint PUT /agents/conversations/{conversation_id}/queue/{entry_id}
 */
async ({ conversationId, entryId, updateQueueEntryRequest }: { conversationId?: string, entryId?: string, updateQueueEntryRequest?: UpdateQueueEntryRequest } = {}): Promise<QueueEntryResponse> => {
        const response = await axios.put(`/agents/conversations/${conversationId}/queue/${entryId}`, updateQueueEntryRequest)
        return ApiResponse.create(response.data, response)},
      delete: /**
 * Delete a queued message
 *
 * Remove a message from the queue and its persisted user message.
 *
 * @endpoint DELETE /agents/conversations/{conversation_id}/queue/{entry_id}
 */
async ({ conversationId, entryId }: { conversationId?: string, entryId?: string } = {}) => {
        const response = await axios.delete(`/agents/conversations/${conversationId}/queue/${entryId}`)
        return ApiResponse.create(response.data, response)},
      get: /**
 * List queued messages for a conversation
 *
 * Return all pending messages in the conversation's queue.
 *
 * @endpoint GET /agents/conversations/{conversation_id}/queue
 */
async ({ conversationId }: { conversationId?: string } = {}): Promise<QueueListResponse> => {
        const response = await axios.get(`/agents/conversations/${conversationId}/queue`)
        return ApiResponse.create(response.data, response)},
      editing: /**
 * Mark a queued message as being edited (holds the queue)
 *
 * Set a queue entry to ``editing`` status.
 * 
 * The drain loop will not dequeue this entry or any entries after it
 * until the entry is marked ``ready`` again via the ready endpoint.
 *
 * @endpoint POST /agents/conversations/{conversation_id}/queue/{entry_id}/editing
 */
async ({ conversationId, entryId }: { conversationId?: string, entryId?: string } = {}): Promise<QueueEntryResponse> => {
        const response = await axios.post(`/agents/conversations/${conversationId}/queue/${entryId}/editing`, {})
        return ApiResponse.create(response.data, response)},
      ready: /**
 * Mark a queued message as ready (unblocks the queue)
 *
 * Set a queue entry back to ``ready`` status and restart the drain loop.
 * 
 * If the drain loop had stopped because this entry was ``editing``, a new
 * drain loop is kicked off so processing resumes immediately.
 *
 * @endpoint POST /agents/conversations/{conversation_id}/queue/{entry_id}/ready
 */
async ({ conversationId, entryId }: { conversationId?: string, entryId?: string } = {}): Promise<QueueEntryResponse> => {
        const response = await axios.post(`/agents/conversations/${conversationId}/queue/${entryId}/ready`, {})
        return ApiResponse.create(response.data, response)}
    },
    activeRun: /**
 * Get the active run ID for a conversation
 *
 * Return the currently-active run_id for a conversation, or null.
 * 
 * Used by the frontend to reconnect to a running SSE stream after a
 * page refresh. The run_id can be passed to the stream endpoint to
 * replay buffered events.
 *
 * @endpoint GET /agents/conversations/{conversation_id}/active-run
 */
async ({ conversationId }: { conversationId?: string } = {}): Promise<{ [key: string]: any }> => {
        const response = await axios.get(`/agents/conversations/${conversationId}/active-run`)
        return ApiResponse.create(response.data, response)},
    scan: /**
 * Scan Conversation
 *
 * Cold-scan a conversation for quality problems; create auto_detected
 * feedback rows for each finding. The mining engine for the backlog.
 *
 * @endpoint POST /agents/conversations/{conversation_id}/scan
 */
async ({ conversationId }: { conversationId?: string } = {}): Promise<JudgeScanResult> => {
        const response = await axios.post(`/agents/conversations/${conversationId}/scan`, {})
        return ApiResponse.create(response.data, response)}
  },
  artifacts: {
    get: /**
 * Get Artifact
 *
 * Get a single artifact by ID.
 *
 * @endpoint GET /agents/artifacts/{artifact_id}
 */
async ({ artifactId }: { artifactId?: string } = {}): Promise<ArtifactResponse> => {
        const response = await axios.get(`/agents/artifacts/${artifactId}`)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Artifact
 *
 * Delete an artifact.
 *
 * @endpoint DELETE /agents/artifacts/{artifact_id}
 */
async ({ artifactId }: { artifactId?: string } = {}) => {
        const response = await axios.delete(`/agents/artifacts/${artifactId}`)
        return ApiResponse.create(response.data, response)},
    rename: /**
 * Rename Artifact
 *
 * Rename an artifact.
 *
 * @endpoint PATCH /agents/artifacts/{artifact_id}/rename
 */
async ({ artifactId, artifactRename }: { artifactId?: string, artifactRename?: ArtifactRename } = {}): Promise<ArtifactResponse> => {
        const response = await axios.patch(`/agents/artifacts/${artifactId}/rename`, artifactRename)
        return ApiResponse.create(response.data, response)},
    source: /**
 * Update Artifact Source
 *
 * Replace artifact source data (user-side edit).
 *
 * @endpoint PUT /agents/artifacts/{artifact_id}/source
 */
async ({ artifactId, artifactSourceUpdate }: { artifactId?: string, artifactSourceUpdate?: ArtifactSourceUpdate } = {}): Promise<ArtifactResponse> => {
        const response = await axios.put(`/agents/artifacts/${artifactId}/source`, artifactSourceUpdate)
        return ApiResponse.create(response.data, response)},
    export_: /**
 * Export Artifact
 *
 * Export artifact to a downloadable file (docx/xlsx/pdf).
 * 
 * NOTE: sync ``def`` (not ``async def``) so FastAPI runs it in a thread pool,
 * which has no running event loop — required because ``_upload`` uses
 * ``asyncio.run()`` internally.
 *
 * @endpoint POST /agents/artifacts/{artifact_id}/export
 */
async ({ artifactId, fmt = 'docx' }: { artifactId?: string, fmt?: string } = {}): Promise<ArtifactResponse> => {
        const response = await axios.post(`/agents/artifacts/${artifactId}/export`, {}, { params: {fmt} })
        return ApiResponse.create(response.data, response)}
  },
  datastores: {
    getByDatastoreId: /**
 * Get Datastore
 *
 * Get a datastore by ID.
 *
 * @endpoint GET /agents/datastores/{datastore_id}
 */
async ({ datastoreId }: { datastoreId?: string } = {}): Promise<DatastoreResponse> => {
        const response = await axios.get(`/agents/datastores/${datastoreId}`)
        return ApiResponse.create(response.data, response)},
    patch: /**
 * Update Datastore
 *
 * Update a datastore.
 *
 * @endpoint PATCH /agents/datastores/{datastore_id}
 */
async ({ datastoreId, datastoreUpdate }: { datastoreId?: string, datastoreUpdate?: DatastoreUpdate } = {}): Promise<DatastoreResponse> => {
        const response = await axios.patch(`/agents/datastores/${datastoreId}`, datastoreUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Datastore
 *
 * Delete a datastore.
 *
 * @endpoint DELETE /agents/datastores/{datastore_id}
 */
async ({ datastoreId }: { datastoreId?: string } = {}) => {
        const response = await axios.delete(`/agents/datastores/${datastoreId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Datastore
 *
 * Create a new datastore plugin.
 *
 * @endpoint POST /agents/datastores
 */
async ({ datastoreCreate }: { datastoreCreate?: DatastoreCreate } = {}): Promise<DatastoreResponse> => {
        const response = await axios.post('/agents/datastores', datastoreCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Datastores
 *
 * List all datastores.
 *
 * @endpoint GET /agents/datastores
 */
async ({ _isActive, skip, limit = 100 }: { _isActive?: boolean, skip?: number, limit?: number } = {}): Promise<ApiResponse<DatastoreResponse[]>> => {
        const response = await axios.get('/agents/datastores', { params: {'is_active': _isActive, skip, limit} })
        return ApiResponse.create(response.data, response)},
    add: /**
 * Add Agent Datastores
 *
 * Connect datastores to an agent.
 *
 * @endpoint POST /agents/{agent_id}/datastores/add
 */
async ({ agentId, agentDatastoresUpdate }: { agentId?: string, agentDatastoresUpdate?: AgentDatastoresUpdate } = {}): Promise<AgentResponse> => {
        const response = await axios.post(`/agents/${agentId}/datastores/add`, agentDatastoresUpdate)
        return ApiResponse.create(response.data, response)},
    remove: /**
 * Remove Agent Datastores
 *
 * Disconnect datastores from an agent.
 *
 * @endpoint POST /agents/{agent_id}/datastores/remove
 */
async ({ agentId, agentDatastoresUpdate }: { agentId?: string, agentDatastoresUpdate?: AgentDatastoresUpdate } = {}): Promise<AgentResponse> => {
        const response = await axios.post(`/agents/${agentId}/datastores/remove`, agentDatastoresUpdate)
        return ApiResponse.create(response.data, response)}
  },
  feedback: {
    getByFeedbackId: /**
 * Get Feedback
 *
 * @endpoint GET /agents/feedback/{feedback_id}
 */
async ({ feedbackId }: { feedbackId?: string } = {}): Promise<AgentFeedbackRead> => {
        const response = await axios.get(`/agents/feedback/${feedbackId}`)
        return ApiResponse.create(response.data, response)},
    patch: /**
 * Update Feedback
 *
 * Triage / edit a feedback item (assign layer, status, repro point).
 *
 * @endpoint PATCH /agents/feedback/{feedback_id}
 */
async ({ feedbackId, feedbackUpdate }: { feedbackId?: string, feedbackUpdate?: FeedbackUpdate } = {}): Promise<AgentFeedbackRead> => {
        const response = await axios.patch(`/agents/feedback/${feedbackId}`, feedbackUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Feedback
 *
 * @endpoint DELETE /agents/feedback/{feedback_id}
 */
async ({ feedbackId }: { feedbackId?: string } = {}) => {
        const response = await axios.delete(`/agents/feedback/${feedbackId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Feedback
 *
 * Log a piece of conversation feedback (customer / QA / auto-detected).
 *
 * @endpoint POST /agents/feedback
 */
async ({ feedbackCreate }: { feedbackCreate?: FeedbackCreate } = {}): Promise<AgentFeedbackRead> => {
        const response = await axios.post('/agents/feedback', feedbackCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Feedback
 *
 * List feedback with optional filters.
 *
 * @endpoint GET /agents/feedback
 */
async ({ _agentId, source, layer, status, offset, limit = 100 }: { _agentId?: string, source?: FeedbackSource, layer?: FeedbackLayer, status?: FeedbackStatus, offset?: number, limit?: number } = {}): Promise<ApiResponse<AgentFeedbackRead[]>> => {
        const response = await axios.get('/agents/feedback', { params: {'agent_id': _agentId, source, layer, status, offset, limit} })
        return ApiResponse.create(response.data, response)},
    aggregate: /**
 * Aggregate Feedback
 *
 * Roll up feedback counts grouped by layer | status | source.
 *
 * @endpoint GET /agents/feedback/aggregate
 */
async ({ by = 'layer', _agentId }: { by?: string, _agentId?: string } = {}): Promise<ApiResponse<FeedbackAggregateRow[]>> => {
        const response = await axios.get('/agents/feedback/aggregate', { params: {by, 'agent_id': _agentId} })
        return ApiResponse.create(response.data, response)},
    reproduce: /**
 * Reproduce Feedback
 *
 * Replay the conversation prefix to confirm the issue still occurs.
 *
 * @endpoint POST /agents/feedback/{feedback_id}/reproduce
 */
async ({ feedbackId }: { feedbackId?: string } = {}): Promise<FeedbackReproResult> => {
        const response = await axios.post(`/agents/feedback/${feedbackId}/reproduce`, {})
        return ApiResponse.create(response.data, response)},
    resolve: /**
 * Resolve Feedback
 *
 * Judge-gated resolution: re-run the prefix; flip to RESOLVED only if the
 * attribution judge confirms the problem no longer occurs.
 *
 * @endpoint POST /agents/feedback/{feedback_id}/resolve
 */
async ({ feedbackId }: { feedbackId?: string } = {}): Promise<FeedbackReproResult> => {
        const response = await axios.post(`/agents/feedback/${feedbackId}/resolve`, {})
        return ApiResponse.create(response.data, response)},
    attribute: /**
 * Attribute Feedback
 *
 * Run the attribution judge on an existing feedback item: read the trace,
 * assign the failing layer + suggested fix, move to TRIAGED.
 *
 * @endpoint POST /agents/feedback/{feedback_id}/attribute
 */
async ({ feedbackId }: { feedbackId?: string } = {}): Promise<JudgeAttributeResult> => {
        const response = await axios.post(`/agents/feedback/${feedbackId}/attribute`, {})
        return ApiResponse.create(response.data, response)}
  },
  mcpServers: {
    getByPluginId: /**
 * Get Mcp Server
 *
 * Get an MCP server plugin by ID.
 *
 * @endpoint GET /agents/mcp-servers/{plugin_id}
 */
async ({ pluginId }: { pluginId?: string } = {}): Promise<McpServerPluginResponse> => {
        const response = await axios.get(`/agents/mcp-servers/${pluginId}`)
        return ApiResponse.create(response.data, response)},
    patch: /**
 * Update Mcp Server
 *
 * Update an MCP server plugin.
 *
 * @endpoint PATCH /agents/mcp-servers/{plugin_id}
 */
async ({ pluginId, mcpServerPluginUpdate }: { pluginId?: string, mcpServerPluginUpdate?: McpServerPluginUpdate } = {}): Promise<McpServerPluginResponse> => {
        const response = await axios.patch(`/agents/mcp-servers/${pluginId}`, mcpServerPluginUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Mcp Server
 *
 * Delete an MCP server plugin.
 *
 * @endpoint DELETE /agents/mcp-servers/{plugin_id}
 */
async ({ pluginId }: { pluginId?: string } = {}) => {
        const response = await axios.delete(`/agents/mcp-servers/${pluginId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Mcp Server
 *
 * Create a new MCP server plugin.
 *
 * @endpoint POST /agents/mcp-servers
 */
async ({ mcpServerPluginCreate }: { mcpServerPluginCreate?: McpServerPluginCreate } = {}): Promise<McpServerPluginResponse> => {
        const response = await axios.post('/agents/mcp-servers', mcpServerPluginCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Mcp Servers
 *
 * List all attachable MCP servers — external connectors AND in-process MCPs.
 * 
 * Both kinds are referenceable by ``name`` in ``agent.mcp_servers``. Filter
 * with ``?source=connector`` or ``?source=builtin``.
 *
 * @endpoint GET /agents/mcp-servers
 */
async ({ _isActive, skip, limit = 100, source }: { _isActive?: boolean, skip?: number, limit?: number, source?: string } = {}): Promise<ApiResponse<McpServerItem[]>> => {
        const response = await axios.get('/agents/mcp-servers', { params: {'is_active': _isActive, skip, limit, source} })
        return ApiResponse.create(response.data, response)},
    sync: /**
 * Sync Mcp Server
 *
 * Re-fetch the tool listing from the remote MCP server.
 * 
 * Use when the remote server has added, removed, or changed tools.
 * The cached ``tool_catalog`` is updated in place.
 *
 * @endpoint POST /agents/mcp-servers/{plugin_id}/sync
 */
async ({ pluginId }: { pluginId?: string } = {}): Promise<McpServerPluginResponse> => {
        const response = await axios.post(`/agents/mcp-servers/${pluginId}/sync`, {})
        return ApiResponse.create(response.data, response)},
    add: /**
 * Add Agent Mcp Servers
 *
 * Connect MCP server plugins to an agent.
 *
 * @endpoint POST /agents/{agent_id}/mcp-servers/add
 */
async ({ agentId, agentMcpServersUpdate }: { agentId?: string, agentMcpServersUpdate?: AgentMcpServersUpdate } = {}): Promise<AgentResponse> => {
        const response = await axios.post(`/agents/${agentId}/mcp-servers/add`, agentMcpServersUpdate)
        return ApiResponse.create(response.data, response)},
    remove: /**
 * Remove Agent Mcp Servers
 *
 * Disconnect MCP server plugins from an agent.
 *
 * @endpoint POST /agents/{agent_id}/mcp-servers/remove
 */
async ({ agentId, agentMcpServersUpdate }: { agentId?: string, agentMcpServersUpdate?: AgentMcpServersUpdate } = {}): Promise<AgentResponse> => {
        const response = await axios.post(`/agents/${agentId}/mcp-servers/remove`, agentMcpServersUpdate)
        return ApiResponse.create(response.data, response)}
  },
  memory: {
    patch: /**
 * Update Memory
 *
 * Update agent memory file.
 *
 * @endpoint PATCH /agents/{agent_id}/memory/{file_path}
 */
async ({ agentId, filePath, memoryUpdate }: { agentId?: string, filePath?: string, memoryUpdate?: MemoryUpdate } = {}): Promise<MemoryResponse> => {
        const response = await axios.patch(`/agents/${agentId}/memory/${filePath}`, memoryUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Memory
 *
 * Delete agent memory file.
 *
 * @endpoint DELETE /agents/{agent_id}/memory/{file_path}
 */
async ({ agentId, filePath }: { agentId?: string, filePath?: string } = {}) => {
        const response = await axios.delete(`/agents/${agentId}/memory/${filePath}`)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Memory
 *
 * List all memory files for an agent.
 *
 * @endpoint GET /agents/{agent_id}/memory
 */
async ({ agentId }: { agentId?: string } = {}): Promise<ApiResponse<MemoryResponse[]>> => {
        const response = await axios.get(`/agents/${agentId}/memory`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Memory
 *
 * Create agent memory file.
 *
 * @endpoint POST /agents/memory
 */
async ({ memoryCreate }: { memoryCreate?: MemoryCreate } = {}): Promise<MemoryResponse> => {
        const response = await axios.post('/agents/memory', memoryCreate)
        return ApiResponse.create(response.data, response)}
  },
  scenarios: {
    getByScenarioId: /**
 * Get Scenario
 *
 * Get a single scenario.
 *
 * @endpoint GET /agents/scenarios/{scenario_id}
 */
async ({ scenarioId }: { scenarioId?: string } = {}): Promise<ScenarioResponse> => {
        const response = await axios.get(`/agents/scenarios/${scenarioId}`)
        return ApiResponse.create(response.data, response)},
    patch: /**
 * Update Scenario
 *
 * Update a scenario.
 *
 * @endpoint PATCH /agents/scenarios/{scenario_id}
 */
async ({ scenarioId, scenarioUpdate }: { scenarioId?: string, scenarioUpdate?: ScenarioUpdate } = {}): Promise<ScenarioResponse> => {
        const response = await axios.patch(`/agents/scenarios/${scenarioId}`, scenarioUpdate)
        return ApiResponse.create(response.data, response)},
    delete: /**
 * Delete Scenario
 *
 * Delete a scenario.
 *
 * @endpoint DELETE /agents/scenarios/{scenario_id}
 */
async ({ scenarioId }: { scenarioId?: string } = {}) => {
        const response = await axios.delete(`/agents/scenarios/${scenarioId}`)
        return ApiResponse.create(response.data, response)},
    post: /**
 * Create Scenario
 *
 * Create a new scenario.
 *
 * @endpoint POST /agents/scenarios
 */
async ({ scenarioCreate }: { scenarioCreate?: ScenarioCreate } = {}): Promise<ScenarioResponse> => {
        const response = await axios.post('/agents/scenarios', scenarioCreate)
        return ApiResponse.create(response.data, response)},
    get: /**
 * List Scenarios
 *
 * List scenarios, optionally filtered by agent.
 *
 * @endpoint GET /agents/scenarios
 */
async ({ _agentId, skip, limit = 100 }: { _agentId?: string, skip?: number, limit?: number } = {}): Promise<ApiResponse<ScenarioResponse[]>> => {
        const response = await axios.get('/agents/scenarios', { params: {'agent_id': _agentId, skip, limit} })
        return ApiResponse.create(response.data, response)},
    messages: {
      getByMessageId: /**
 * Get Message
 *
 * Get a single message by ID.
 *
 * @endpoint GET /agents/scenarios/{scenario_id}/messages/{message_id}
 */
async ({ scenarioId, messageId }: { scenarioId?: string, messageId?: string } = {}): Promise<ScenarioMessageData> => {
        const response = await axios.get(`/agents/scenarios/${scenarioId}/messages/${messageId}`)
        return ApiResponse.create(response.data, response)},
      patch: /**
 * Update Message
 *
 * Partially update a message.
 *
 * @endpoint PATCH /agents/scenarios/{scenario_id}/messages/{message_id}
 */
async ({ scenarioId, messageId, scenarioMessageUpdate }: { scenarioId?: string, messageId?: string, scenarioMessageUpdate?: ScenarioMessageUpdate } = {}): Promise<ScenarioMessageData> => {
        const response = await axios.patch(`/agents/scenarios/${scenarioId}/messages/${messageId}`, scenarioMessageUpdate)
        return ApiResponse.create(response.data, response)},
      delete: /**
 * Delete Message
 *
 * Delete a message from a scenario.
 *
 * @endpoint DELETE /agents/scenarios/{scenario_id}/messages/{message_id}
 */
async ({ scenarioId, messageId }: { scenarioId?: string, messageId?: string } = {}) => {
        const response = await axios.delete(`/agents/scenarios/${scenarioId}/messages/${messageId}`)
        return ApiResponse.create(response.data, response)},
      post: /**
 * Add Message
 *
 * Append a message to a scenario.
 *
 * @endpoint POST /agents/scenarios/{scenario_id}/messages
 */
async ({ scenarioId, scenarioMessageData }: { scenarioId?: string, scenarioMessageData?: ScenarioMessageData } = {}): Promise<ScenarioMessageData> => {
        const response = await axios.post(`/agents/scenarios/${scenarioId}/messages`, scenarioMessageData)
        return ApiResponse.create(response.data, response)},
      get: /**
 * List Messages
 *
 * List all messages in a scenario.
 *
 * @endpoint GET /agents/scenarios/{scenario_id}/messages
 */
async ({ scenarioId }: { scenarioId?: string } = {}): Promise<ApiResponse<ScenarioMessageData[]>> => {
        const response = await axios.get(`/agents/scenarios/${scenarioId}/messages`)
        return ApiResponse.create(response.data, response)},
      run: /**
 * Run Message
 *
 * Generate an agent response for the given user message.
 * 
 * The agent receives the full conversation history up to this message as
 * context. Any existing assistant messages immediately following are
 * replaced. Returns the newly generated assistant message.
 *
 * @endpoint POST /agents/scenarios/{scenario_id}/messages/{message_id}/run
 */
async ({ scenarioId, messageId }: { scenarioId?: string, messageId?: string } = {}): Promise<ScenarioMessageData> => {
        const response = await axios.post(`/agents/scenarios/${scenarioId}/messages/${messageId}/run`, {})
        return ApiResponse.create(response.data, response)}
    }
  }
};

/**
 * API functions for checkDb endpoints
 */
export const checkDb = /**
 * Check Db
 *
 * @endpoint GET /check-db
 */
async (): Promise<{ [key: string]: any }> => {
        const response = await axios.get('/check-db')
        return ApiResponse.create(response.data, response)};

/**
 * API functions for project endpoints
 */
export const project = {
  get: /**
 * Get Project Info
 *
 * Get project metadata and configuration.
 * 
 * Returns basic information about the current project including
 * name, version, environment, deployment type, and module counts.
 *
 * @endpoint GET /project/
 */
async (): Promise<{ [key: string]: any }> => {
        const response = await axios.get('/project/')
        return ApiResponse.create(response.data, response)},
  modules: /**
 * List Installed Modules
 *
 * List all installed modules with their metadata.
 * 
 * Returns detailed information about each registered module including:
 * - name, display_name, icon, color
 * - description, stability
 * - dependencies and extensions
 * - enabled status
 * - router prefix (if applicable)
 *
 * @endpoint GET /project/modules
 */
async (): Promise<ApiResponse<{ [key: string]: any }[]>> => {
        const response = await axios.get('/project/modules')
        return ApiResponse.create(response.data, response)},
  health: /**
 * Project Health Check
 *
 * Health check endpoint with module dependency validation.
 * 
 * Returns health status and checks if all module dependencies
 * are satisfied.
 *
 * @endpoint GET /project/health
 */
async (): Promise<{ [key: string]: any }> => {
        const response = await axios.get('/project/health')
        return ApiResponse.create(response.data, response)}
};

/**
 * Aggregated API object containing all endpoints
 */
export const api = {
	events,
	person,
	authentication,
	admin,
	blox,
	datastore,
	form,
	newsletter,
	files,
	automations,
	agents,
	checkDb,
	project,
};


// ============================================================================
// API Instance & Configuration
// ============================================================================

/**
 * Last response metadata (accessible via useApi().$lastResponse)
 */
const lastResponse = ref<AxiosResponse | null>(null)

/**
 * API instance type with enhanced response objects
 */
type ApiInstance = typeof api & {
	$raw: typeof api
	$lastResponse: Ref<AxiosResponse | null>
	$axios: typeof axios
	$ApiResponse: typeof ApiResponse
	$config: Ref<ApiConfig | null>
}

// Global state
let apiInstance: ApiInstance | null = null
let apiConfig = ref<ApiConfig | null>(null)
let _streamBaseURL: string | undefined

/** Resolve base URL for SSE stream endpoints. Uses streamBaseURL if set, otherwise falls back to axios baseURL. */
export function getStreamBaseURL(): string {
	return _streamBaseURL || axios.defaults.baseURL || ""
}

/**
 * Create and configure the API instance
 * @param config - API configuration options
 * @returns Configured API instance
 */
export function createApi(config: ApiConfig = {}): ApiInstance {
	// Store config for access via useApi().$config
	apiConfig.value = config

	// Configure axios defaults
	if (config.baseURL) {
		axios.defaults.baseURL = config.baseURL
	}
	_streamBaseURL = config.streamBaseURL
	if (config.withCredentials !== undefined) {
		axios.defaults.withCredentials = config.withCredentials
	}
	if (config.axiosConfig) {
		Object.assign(axios.defaults, config.axiosConfig)
	}

	// Clear existing interceptors
	axios.interceptors.request.clear()
	axios.interceptors.response.clear()

	// Setup auth interceptor
	if (config.auth) {
		axios.interceptors.request.use(async (reqConfig) => {
			const token = await config.auth!.getToken?.()
			if (token) {
				const prefix = config.auth!.tokenPrefix ?? 'Bearer'
				reqConfig.headers.Authorization = `${prefix} ${token}`
			}
			return reqConfig
		})
	}

	// Setup custom request interceptor
	if (config.interceptors?.request) {
		axios.interceptors.request.use(config.interceptors.request)
	}

	// Setup response interceptor for error handling
	axios.interceptors.response.use(
		(response) => {
			// Store last response for metadata access
			lastResponse.value = response
			
			// Apply custom response interceptor if provided
			if (config.interceptors?.response) {
				return config.interceptors.response(response)
			}
			return response
		},
		async (error) => {
			// Handle 401 Unauthorized
			if (error.response?.status === 401) {
				await config.auth?.onUnauthorized?.()
			}

			// Parse and handle error
			const parsedError = parseApiError(error)
			config.onError?.(parsedError, error)

			throw error
		}
	)

	// API already returns ApiResponse directly!
	// Just add the special properties
	apiInstance = {
		...api,
		$raw: api,
		$lastResponse: lastResponse,
		$axios: axios,
		$ApiResponse: ApiResponse,
		$config: apiConfig
	} as ApiInstance

	return apiInstance
}

/**
 * Access the configured API instance
 * @returns API instance with enhanced response objects
 * @throws Error if createApi hasn't been called
 */
export function useApi(): ApiInstance {
	if (!apiInstance) {
		throw new Error(
			'API not initialized. Call createApi() first.\n\n' +
			'Example:\n' +
			'import { createApi } from \'./generated/api\'\n' +
			'createApi({ baseURL: \'/api\' })'
		)
	}
	return apiInstance
}

// ============================================================================
// Re-export utilities for convenience
// ============================================================================

export {
	parseApiError,
	formatFieldErrors,
	useApiRequest,
	useCancellableRequest,
	unwrap,
	ApiResponse,
	type ApiConfig,
	type ParsedError,
	type ResponseMetadata,
	type ApiRequestState,
	type FastAPIValidationError,
	type FastAPIErrorResponse,
	type RequestInterceptor,
	type ResponseInterceptor,
	type Unwrapped
}
